// The insides of the town's buildings round the plaza (room.ts draws the page):
// a dollhouse cutaway of each, the back and left walls standing, the front and
// right cut down to low stubs with a doorway at the front, like the Dance Hall.
// Each set builds its furniture from the house builder's parts (furniture.ts,
// kit.ts) and a few things of its own, and puts what the page knows on its
// walls: postcards on the Post Office's, today's headline on the drying line,
// the tally on the Workshop's board, the menu on the café's chalkboard.
// Plazmi University's is one screening room: a big screen behind a marble
// proscenium, rows of red seats, and a projector at the back whose soft beam
// falls on the screen. Those watching a lesson sit in the rows; the others
// stand about at the back and in the aisles.
import * as THREE from 'three';
import { mat, cyl, sphere, rbox, lathe, at } from './kit.js';
import { wbox } from './house.js';
import { surf } from './surfaces.js';
import * as F from './furniture.js';
import { letters } from './place.js';
import type { RoomId } from './townpages.js';
import type { Seat, IdleGround } from './crowd.js';

export type P = [number, number];
export type Rect = { x: number; y: number; w: number; d: number };
export type Built = {
  avoid: Rect[];                       // furniture footprints the plazmi keep clear of
  starts: P[];                         // where plazmi stand about when the page opens
  seats?: { at: P; face: number }[];   // chairs a plazmi may sit on (the café's)
  update?: (d: any) => void;           // the room's live data, as it changes
  tick?: (dt: number, t: number) => void;
  lights: THREE.Light[];
  // (PU) who's at the place, as it changes (the screen shows what they're watching)
  hear?: (present: { slug: string; lesson?: Lesson | null }[], now: number) => void;
  theatre?: Seat[];                    // the seats those watching a lesson take, best first
  idle?: IdleGround;                   // where the others stand about
};
// a lesson someone is watching (/api/place/town.pu/live)
export type Lesson = { course: string; n: number; title: string; until: number };
export type RoomSet = {
  W: number; D: number; wall: number; door: [number, number];
  plaster: string; paper: { bg: string; ink: string; ink2: string; kind: 'stripe' | 'sprig' | 'dots' | 'diamond' | 'stars' };
  floor: { kind: string; color: string; board?: number };
  wainscot: string; trim: string; island: string;
  windows: number[];                   // where the windows are along the left wall (z)
  build: (g: THREE.Group) => Built;
  frame?: { dist: number; y: number }; // a bigger room framed from further off (the page's camera)
};

const W0 = 13, D0 = 10, WALL0 = 4.2;

// ---------------------------------------------------------------------------
// little things

function canvasTex(w: number, h: number, draw: (g: CanvasRenderingContext2D) => void) {
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  draw(c.getContext('2d')!);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
  return t;
}

function wallpaper(p: RoomSet['paper']) {
  const t = canvasTex(256, 256, (g) => {
    g.fillStyle = p.bg; g.fillRect(0, 0, 256, 256);
    if (p.kind === 'stripe') { g.fillStyle = p.ink; for (let x = 0; x < 256; x += 64) g.fillRect(x, 0, 22, 256); g.fillStyle = p.ink2; for (let x = 40; x < 256; x += 64) g.fillRect(x, 0, 3, 256); }
    if (p.kind === 'dots') { for (let y = 0; y < 256; y += 32) for (let x = (y / 32) % 2 ? 16 : 0; x < 256; x += 32) { g.fillStyle = (x + y) % 64 ? p.ink : p.ink2; g.beginPath(); g.arc(x + 8, y + 8, 4, 0, 7); g.fill(); } }
    if (p.kind === 'diamond') { g.strokeStyle = p.ink; g.lineWidth = 3; for (let i = -256; i < 512; i += 48) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i + 256, 256); g.stroke(); g.beginPath(); g.moveTo(i + 256, 0); g.lineTo(i, 256); g.stroke(); } g.fillStyle = p.ink2; for (let y = 0; y < 256; y += 48) for (let x = 0; x < 256; x += 48) { g.beginPath(); g.arc(x + 24, y + 24, 3, 0, 7); g.fill(); } }
    if (p.kind === 'sprig' || p.kind === 'stars') {
      for (let k = 0; k < 8; k++) {
        const x = (k * 97) % 256, y = (k * 61 + (k % 2) * 40) % 256;
        g.save(); g.translate(x, y); g.rotate(k);
        if (p.kind === 'sprig') {
          g.strokeStyle = p.ink; g.lineWidth = 2; g.beginPath(); g.moveTo(0, -10); g.quadraticCurveTo(3, 0, 0, 10); g.stroke();
          g.fillStyle = p.ink; for (const s of [-1, 1]) { g.beginPath(); g.ellipse(s * 5, -2 + s, 5, 2.5, s * 0.6, 0, 7); g.fill(); }
          g.fillStyle = p.ink2; g.beginPath(); g.arc(0, -11, 3, 0, 7); g.fill();
        } else {
          g.fillStyle = k % 2 ? p.ink : p.ink2; g.beginPath();
          for (let i = 0; i < 10; i++) { const a = (i / 10) * Math.PI * 2, r = i % 2 ? 4 : 10; g.lineTo(Math.cos(a) * r, Math.sin(a) * r); }
          g.fill();
        }
        g.restore();
      }
    }
  });
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(0.75, 0.75);
  return mat('#ffffff', { map: t, rough: 0.92 });
}

function skyTex() {
  return canvasTex(128, 256, (g) => {
    const gr = g.createLinearGradient(0, 0, 0, 256);
    gr.addColorStop(0, '#7fb2e0'); gr.addColorStop(0.6, '#bfdcef'); gr.addColorStop(1, '#f6dcb8');
    g.fillStyle = gr; g.fillRect(0, 0, 128, 256);
    g.fillStyle = 'rgba(255,255,255,.85)';
    for (const [x, y, r] of [[30, 70, 14], [46, 64, 18], [64, 72, 12], [90, 150, 10], [104, 146, 14]]) { g.beginPath(); g.arc(x, y, r, 0, 7); g.fill(); }
    g.fillStyle = '#86a75e'; g.fillRect(0, 226, 128, 30);
  });
}

// a sign or a board whose words the page keeps up to date
export class Board {
  mesh: THREE.Mesh;
  private m: THREE.MeshStandardMaterial;
  private key = '';
  constructor(public w: number, public h: number, private px: [number, number], private bg: string | null, glow = 0) {
    this.m = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.9, transparent: !bg, emissive: new THREE.Color('#ffffff'), emissiveIntensity: glow });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), this.m);
    this.mesh.userData.keep = true; this.mesh.userData.noShadow = true;
  }
  set(lines: Parameters<typeof letters>[0], opts: Parameters<typeof letters>[4] = {}) {
    const k = JSON.stringify(lines); if (k === this.key) return; this.key = k;
    this.m.map?.dispose();
    this.m.map = letters(lines, this.px[0], this.px[1], this.bg, opts);
    if (this.m.emissiveIntensity) this.m.emissiveMap = this.m.map;
    this.m.needsUpdate = true;
  }
}

// a sheet of paper with lines of "type" on it, and a headline if it has one
function sheet(seed: number, head = '') {
  return canvasTex(160, 220, (g) => {
    g.fillStyle = '#f7f1e3'; g.fillRect(0, 0, 160, 220);
    g.fillStyle = '#1d4f91'; g.font = '800 15px ui-rounded, system-ui, sans-serif'; g.textAlign = 'center'; g.fillText('THE PLAZMI GAZETTE', 80, 22);
    g.fillStyle = '#2b2622'; g.fillRect(10, 30, 140, 2);
    if (head) { g.font = '800 17px Georgia, serif'; wrap(g, head, 80, 52, 140, 19, 3); }
    let y = head ? 112 : 44;
    for (let i = 0; y < 210; i++, y += 7) { g.fillStyle = 'rgba(40,34,30,.45)'; const w2 = i % 9 === 8 ? 60 : 64; g.fillRect(10, y, w2, 3); g.fillRect(86, y, i % 7 === 6 ? 40 : 64, 3); }
    if (!head) { g.fillStyle = '#c9bfa8'; g.fillRect(86, 44 + (seed % 3) * 30, 64, 40); }
  });
}
function wrap(g: CanvasRenderingContext2D, text: string, x: number, y: number, max: number, lh: number, n: number) {
  const words = text.split(' '); let line = '', k = 0;
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (g.measureText(t).width > max && line) { g.fillText(line, x, y + k * lh); line = w; if (++k >= n) return; } else line = t;
  }
  if (line && k < n) g.fillText(line, x, y + k * lh);
}

function crate(c: string, s = 0.5) {
  const g = new THREE.Group();
  g.add(rbox(s, s * 0.6, s * 0.8, 0.02, mat('#b98a56', { rough: 0.8 }), 1).translateY(s * 0.3));
  for (let i = 0; i < 6; i++) { const b = sphere(s * 0.13, mat(c, { rough: 0.5 }), 10, 8); b.position.set((i % 3 - 1) * s * 0.28, s * 0.62, (Math.floor(i / 3) - 0.5) * s * 0.3); g.add(b); }
  return g;
}

function stripes(a: string, b: string, n = 10) {
  return canvasTex(256, 16, (g) => { for (let i = 0; i < n; i++) { g.fillStyle = i % 2 ? b : a; g.fillRect((i * 256) / n, 0, 256 / n + 1, 16); } });
}

function awning(w: number, d: number, a: string, b: string) {
  const g = new THREE.Group();
  const m = mat('#ffffff', { map: stripes(a, b, Math.round(w * 4)), rough: 0.8, side: THREE.DoubleSide });
  const r = rbox(w, 0.05, d, 0.02, m, 1); r.rotation.x = 0.35; g.add(r);
  for (let i = 0; i < Math.round(w / 0.3); i++) { const s = sphere(0.14, mat(i % 2 ? b : a, { rough: 0.8 }), 8, 6); s.scale.set(1, 0.6, 0.25); s.position.set(-w / 2 + 0.15 + i * 0.3, -0.18 - Math.sin(0.35) * d / 2 + 0.04, d / 2 * Math.cos(0.35)); g.add(s); }
  return g;
}

function warmLight(c: string, i: number, x: number, y: number, z: number, dist = 9) {
  const l = new THREE.PointLight(c, i, dist, 1.7); l.position.set(x, y, z); return l;
}

// ---------------------------------------------------------------------------
// the shell: floor, two papered walls with wainscot, windows onto the day, the cut-down stubs

export function shell(g: THREE.Group, r: RoomSet) {
  const { W, D, wall: WALL, door } = r;
  const paper = wallpaper(r.paper), plaster = mat(r.plaster, { rough: 0.9 }), wood = mat(r.wainscot, { rough: 0.6 }), trim = mat(r.trim, { rough: 0.45 });
  g.add(wbox(0, -0.02, 0, W, 0.04, D, surf({ kind: r.floor.kind, color: r.floor.color, board: r.floor.board ?? 0.2 })));
  g.add(wbox(-0.3, 0, -0.3, W + 0.3, WALL, 0, [plaster, plaster, plaster, plaster, paper, plaster]));
  g.add(wbox(-0.3, 0, 0, 0, WALL, D + 0.3, [paper, plaster, plaster, plaster, plaster, plaster]));
  g.add(wbox(0, 0, 0, W, 1.0, 0.06, wood), wbox(0, 0, 0, 0.06, 1.0, D, wood));
  g.add(wbox(0, 1.0, 0, W, 1.07, 0.09, trim), wbox(0, 1.0, 0, 0.09, 1.07, D, trim));
  g.add(wbox(-0.3, WALL, -0.3, W + 0.3, WALL + 0.12, 0.05, trim), wbox(-0.3, WALL, 0, 0.05, WALL + 0.12, D + 0.3, trim));
  g.add(wbox(0, WALL - 0.14, 0, W, WALL, 0.1, trim), wbox(0, WALL - 0.14, 0, 0.1, WALL, D, trim));
  for (const [x0, x1] of [[-0.3, door[0]], [door[1], W + 0.3]]) g.add(wbox(x0, 0, D, x1, 0.75, D + 0.3, plaster), wbox(x0, 0.75, D, x1, 0.82, D + 0.3, trim));
  g.add(wbox(W, 0, -0.3, W + 0.3, 0.75, D, plaster), wbox(W, 0.75, -0.3, W + 0.3, 0.82, D, trim));
  g.add(wbox(door[0] + 0.25, 0.02, D - 0.9, door[1] - 0.25, 0.045, D - 0.05, mat('#b8456e', { rough: 1 })));
  // windows along the left wall, onto the day outside
  const sky = new THREE.MeshBasicMaterial({ map: skyTex() });
  for (const z of r.windows) {
    const win = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 1.9), sky);
    win.rotation.y = Math.PI / 2; win.position.set(0.04, 2.35, z); g.add(win);
    g.add(wbox(0.02, 1.32, z - 0.8, 0.16, 1.4, z + 0.8, trim), wbox(0.02, 3.3, z - 0.8, 0.14, 3.38, z + 0.8, trim));
    g.add(wbox(0.02, 1.4, z - 0.8, 0.12, 3.3, z - 0.7, trim), wbox(0.02, 1.4, z + 0.7, 0.12, 3.3, z + 0.8, trim));
    g.add(wbox(0.02, 2.33, z - 0.7, 0.1, 2.38, z + 0.7, trim), wbox(0.02, 1.4, z - 0.03, 0.1, 3.3, z + 0.03, trim));
    // a sill with a little pot on it
    g.add(wbox(0.02, 1.28, z - 0.85, 0.32, 1.34, z + 0.85, trim));
    const pot = F.plant({ kind: 'fern', pot: '#c56f4a', scale: 0.7, seed: Math.round(z) } as any); pot.position.set(0.18, 1.34, z + 0.45); g.add(pot);
  }
}

// ---------------------------------------------------------------------------
// the rooms

function readingRoom(g: THREE.Group): Built {
  const W = W0, D = D0;
  // shelves to the ceiling along the back wall, with a rolling ladder
  for (let i = 0; i < 7; i++) {
    const s = F.bookshelf({ wood: 'walnut', w: 1.15, h: 3.6, d: 0.42, seed: i + 3 } as any); s.position.set(3.4 + i * 1.18, 0, 0.26); g.add(s);
  }
  const rail = mat('#c7a24a', { rough: 0.3, metal: 0.7 });
  g.add(at(cyl(0.025, 0.025, 8.3, rail, 8).rotateZ(Math.PI / 2), 6.95, 3.5, 0.62));
  const lad = new THREE.Group(), lw = mat('#8a5a3a', { rough: 0.6 });
  for (const x of [-0.25, 0.25]) { const s = rbox(0.06, 3.7, 0.06, 0.01, lw, 1); s.position.set(x, 1.85, 0); lad.add(s); }
  for (let k = 0; k < 9; k++) { const r2 = cyl(0.02, 0.02, 0.5, lw, 6); r2.rotation.z = Math.PI / 2; r2.position.set(0, 0.3 + k * 0.4, 0); lad.add(r2); }
  lad.position.set(8.2, 0, 0.95); lad.rotation.x = -0.22; g.add(lad);
  // the fireplace on the left wall, two armchairs and a reading lamp before it
  const fp = F.fireplace({ surround: '#e9dcc6', brick: '#9b4a35', w: 1.7 } as any); fp.position.set(0.2, 0, 5.0); fp.rotation.y = Math.PI / 2; g.add(fp);
  g.add(at(F.rug({ w: 3.4, d: 2.6, field: '#8c3b4f', border: '#2f3f5a', accent: '#e8cf9a' } as any), 2.6, 0.03, 5.0));
  const a1 = F.armchair({ color: '#5f7d6e' } as any); a1.position.set(2.7, 0, 3.6); a1.rotation.y = -0.6 - Math.PI / 2 + Math.PI; g.add(a1);
  const a2 = F.armchair({ color: '#b5654b' } as any); a2.position.set(2.7, 0, 6.5); a2.rotation.y = 0.6 - Math.PI / 2 + Math.PI; g.add(a2);
  a1.rotation.y = -2.2; a2.rotation.y = -0.95;
  const lamp = F.floorLamp({ shade: '#f2d9a6' } as any); lamp.position.set(3.4, 0, 2.7); g.add(lamp);
  const side = F.sideTable({ wood: 'oak', lamp: false } as any); side.position.set(3.0, 0, 5.05); g.add(side);
  for (let i = 0; i < 3; i++) { const b = rbox(0.3, 0.05, 0.22, 0.01, mat(['#2f4a63', '#c9a227', '#8c3b2f'][i]), 1); b.position.set(3.0, 0.65 + i * 0.05, 5.05); b.rotation.y = i * 0.3; g.add(b); }
  // a long reading table with green lamps, and chairs
  const tw = mat('#7a4a2e', { rough: 0.55 });
  g.add(wbox(6.0, 0.74, 4.6, 10.4, 0.8, 5.8, tw));
  for (const [x, z] of [[6.15, 4.75], [10.25, 4.75], [6.15, 5.65], [10.25, 5.65]]) g.add(at(cyl(0.04, 0.04, 0.74, tw, 8), x, 0.37, z));
  for (const x of [7.1, 9.3]) {
    const l = new THREE.Group();
    l.add(cyl(0.012, 0.012, 0.32, mat('#c7a24a', { metal: 0.7, rough: 0.3 }), 6).translateY(0.16));
    const sh = cyl(0.03, 0.1, 0.12, mat('#2f7a4f', { emissive: '#ffe2a0', emissiveIntensity: 0.25, rough: 0.3 }), 14); sh.rotation.z = Math.PI / 2; sh.position.y = 0.34; l.add(sh);
    l.position.set(x, 0.8, 5.2); g.add(l);
    for (const z of [4.1, 6.3]) { const c = F.chair({ wood: 'walnut', seat: '#5f7d6e' } as any); c.position.set(x, 0, z); c.rotation.y = z < 5 ? 0 : Math.PI; g.add(c); }
  }
  // the Gazette's back issues in a rack by the door, and the counting board
  const rack = new THREE.Group();
  rack.add(rbox(1.4, 1.3, 0.35, 0.02, mat('#8a5a3a', { rough: 0.6 }), 1).translateY(0.65));
  for (let i = 0; i < 4; i++) { const p = new THREE.Mesh(new THREE.PlaneGeometry(0.28, 0.38), mat('#ffffff', { map: sheet(i), rough: 0.9 })); p.position.set(-0.5 + i * 0.33, 1.45, 0.06); p.rotation.x = -0.25; rack.add(p); }
  rack.position.set(11.8, 0, 2.2); rack.rotation.y = -Math.PI / 2; g.add(rack);
  const board = new Board(2.2, 1.3, [440, 260], '#2f4a3b');
  board.mesh.position.set(0.15, 2.75, 8.3); board.mesh.rotation.y = Math.PI / 2; g.add(board.mesh);
  g.add(wbox(0.02, 2.05, 7.15, 0.12, 3.45, 9.45, mat('#8a5a3a')));
  for (const [x, z, k] of [[12.3, 0.7, 'monstera'], [0.7, 9.4, 'tree']] as [number, number, string][]) g.add(at(F.plant({ kind: k, scale: 1.5, seed: x } as any), x, 0, z));
  // a round rug with floor cushions and a little stack of books, a globe on a stand
  g.add(at(F.rug({ w: 2.6, d: 2.6, field: '#d9a441', border: '#8c3b4f', accent: '#f6efe0', round: true } as any), 6.6, 0.03, 8.0));
  for (const [x, z, c] of [[5.9, 7.5, '#8c3b4f'], [7.3, 8.6, '#2f5a4a'], [6.0, 8.7, '#5f7d8a']] as [number, number, string][]) { const cu = rbox(0.62, 0.2, 0.62, 0.1, mat(c, { rough: 1 }), 3); cu.position.set(x, 0.1, z); cu.rotation.y = x; g.add(cu); }
  for (let i = 0; i < 5; i++) { const b = rbox(0.34 - i * 0.02, 0.07, 0.26, 0.01, mat(['#2f4a63', '#c9a227', '#8c3b2f', '#41634a', '#e8dcc4'][i]), 1); b.position.set(7.0, 0.035 + i * 0.07, 7.6); b.rotation.y = i * 0.25; g.add(b); }
  const globe = new THREE.Group();
  globe.add(cyl(0.2, 0.24, 0.05, mat('#6e4430'), 14).translateY(0.025), cyl(0.03, 0.03, 0.75, mat('#6e4430'), 8).translateY(0.4));
  const ball = sphere(0.3, mat('#5fa8c8', { rough: 0.5 }), 20, 14); ball.position.y = 1.05; globe.add(ball);
  for (const [a, b2] of [[0.4, 0.3], [2.2, -0.2], [4.0, 0.5]]) { const land = sphere(0.12, mat('#8fbf6a', { rough: 0.7 }), 10, 8); land.scale.set(1.3, 0.8, 0.5); land.position.set(Math.cos(a) * 0.25, 1.05 + b2 * 0.4, Math.sin(a) * 0.25); land.lookAt(0, 1.05, 0); globe.add(land); }
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.015, 6, 28), mat('#c7a24a', { metal: 0.7, rough: 0.3 })); ring.position.y = 1.05; ring.rotation.y = 0.5; globe.add(ring);
  globe.position.set(10.9, 0, 7.6); g.add(globe);
  const fire = warmLight('#ff9a50', 7, 0.9, 0.8, 5.0, 6);
  const reading = warmLight('#ffd9a0', 6, 3.4, 1.6, 2.7, 6);
  return {
    avoid: [{ x: 2.6, y: 0, w: 8.2, d: 0.9 }, { x: 0, y: 3.9, w: 1.1, d: 2.2 }, { x: 2.1, y: 3.0, w: 1.3, d: 1.1 }, { x: 2.1, y: 6.0, w: 1.3, d: 1.1 }, { x: 2.6, y: 4.6, w: 0.8, d: 0.9 },
      { x: 5.8, y: 3.7, w: 4.8, d: 3.1 }, { x: 11.5, y: 1.4, w: 0.7, d: 1.6 }, { x: 3.1, y: 2.4, w: 0.6, d: 0.6 }, { x: 10.6, y: 7.3, w: 0.6, d: 0.6 }, { x: 0.2, y: 8.9, w: 1.0, d: 1.0 }],
    starts: [[4.4, 4.8], [4.2, 7.6], [8.6, 7.4], [11.4, 5.4], [6.4, 2.4], [10.2, 2.6], [2.8, 8.6]],
    lights: [fire, reading],
    update: (d) => {
      board.set([{ text: 'KEPT & TAUGHT', size: 30, color: '#f2c14e' }, { text: `${d.keepsakes_total} keepsakes`, size: 40, color: '#f6f2e8' }, { text: `${d.lessons} dances taught`, size: 40, color: '#f6f2e8' },
        { text: `${(d.journals || []).reduce((a: number, j: any) => a + j.pages, 0)} journal pages, never read`, size: 26, color: '#cfd8cf' }]);
    },
    tick: (_dt, t) => { fire.intensity = 6 + Math.sin(t * 7.3) * 0.9 + Math.sin(t * 13.1) * 0.6; },
  };
}

function postOffice(g: THREE.Group): Built {
  const W = W0, D = D0;
  // the counter, with a brass bell and scales, and a little grille
  const wood = mat('#8a4f36', { rough: 0.55 }), top = mat('#e8d6b4', { rough: 0.4 }), brass = mat('#d6b05a', { rough: 0.3, metal: 0.7 });
  g.add(wbox(7.4, 0, 1.4, 12.2, 1.05, 2.2, wood), wbox(7.3, 1.05, 1.3, 12.3, 1.12, 2.3, top));
  for (let x = 7.7; x < 12.1; x += 0.6) g.add(wbox(x, 0.15, 2.2, x + 0.4, 0.9, 2.23, mat('#a0603f', { rough: 0.6 })));
  g.add(at(lathe([[0, 0], [0.13, 0], [0.12, 0.05], [0.07, 0.12], [0.02, 0.16], [0.03, 0.2], [0, 0.21]], brass, 18), 8.1, 1.12, 1.9));
  g.add(at(cyl(0.2, 0.2, 0.02, brass, 18), 11.2, 1.3, 1.8), at(cyl(0.02, 0.02, 0.2, brass, 6), 11.2, 1.2, 1.8), at(rbox(0.5, 0.06, 0.3, 0.01, brass, 1), 11.2, 1.15, 1.8));
  for (let x = 9.0; x < 10.8; x += 0.12) g.add(wbox(x, 1.12, 1.33, x + 0.02, 2.0, 1.36, brass));
  g.add(wbox(8.9, 2.0, 1.3, 10.9, 2.08, 1.38, brass));
  // pigeonholes behind the counter, a letter here and there
  const pw = mat('#a46b45', { rough: 0.6 });
  g.add(wbox(7.6, 1.5, 0.05, 12.0, 3.9, 0.55, mat('#6e4430')));
  const cols = ['#f7f1e3', '#ffe3ef', '#e3f1ff', '#fff3c4'];
  for (let i = 0; i < 9; i++) for (let j = 0; j < 6; j++) {
    const x = 7.7 + i * 0.47, y = 1.58 + j * 0.38;
    g.add(wbox(x, y, 0.08, x + 0.44, y + 0.03, 0.58, pw), wbox(x, y, 0.08, x + 0.03, y + 0.36, 0.58, pw));
    if ((i * 7 + j * 3) % 4 === 0) { const l = wbox(x + 0.06, y + 0.04, 0.3, x + 0.38, y + 0.2, 0.55, mat(cols[(i + j) % 4], { rough: 0.9 })); l.rotation.z = ((i + j) % 3 - 1) * 0.05; g.add(l); }
  }
  // the postbox by the door: a red pillar box
  const red = mat('#c8312f', { rough: 0.35 });
  const box = new THREE.Group();
  box.add(cyl(0.36, 0.38, 1.4, red, 22).translateY(0.7), cyl(0.42, 0.42, 0.1, red, 22).translateY(1.45));
  const dome = sphere(0.4, red, 20, 10); dome.scale.y = 0.55; dome.position.y = 1.5; box.add(dome);
  box.add(at(rbox(0.36, 0.06, 0.06, 0.02, mat('#1c1414'), 1), 0, 1.15, 0.36), at(cyl(0.42, 0.42, 0.08, mat('#1c1414'), 22), 0, 0.04, 0));
  const plate = new THREE.Mesh(new THREE.PlaneGeometry(0.34, 0.2), mat('#ffffff', { map: letters([{ text: 'POST', size: 40, color: '#f2c14e' }], 128, 72, '#7a1a18') }));
  plate.position.set(0, 0.82, 0.375); box.add(plate);
  box.position.set(11.6, 0, 8.4); g.add(box);
  // a writing desk with stamps and an inkwell
  const desk = F.desk({ wood: 'oak' } as any); desk.position.set(1.1, 0, 7.4); desk.rotation.y = Math.PI / 2; g.add(desk);
  const stool = F.chair({ wood: 'oak', seat: '#c8312f' } as any); stool.position.set(1.9, 0, 7.4); stool.rotation.y = -Math.PI / 2; g.add(stool);
  // the wall of postcards on the back wall: filled from the day's cards
  const cardsG = new THREE.Group(); cardsG.userData.keep = true; g.add(cardsG);
  g.add(wbox(0.4, 1.35, 0.02, 7.0, 3.95, 0.07, mat('#c99a6a', { rough: 0.95 })));
  g.add(wbox(0.3, 1.27, 0.02, 7.1, 1.35, 0.1, mat('#6e4430')), wbox(0.3, 3.95, 0.02, 7.1, 4.03, 0.1, mat('#6e4430')));
  const sign = new Board(3.2, 0.5, [640, 100], '#7a1a18');
  sign.set([{ text: 'POSTCARDS SENT TODAY', size: 52, color: '#fbe9c8' }]);
  sign.mesh.position.set(9.8, 3.25, 1.8); g.add(sign.mesh);
  for (const x of [8.4, 11.2]) g.add(at(cyl(0.008, 0.008, 0.7, mat('#3a2a20'), 4), x, 3.85, 1.8));
  // parcels in brown paper and string, stacked by the counter
  for (const [x, y, z, w2, h2, d2] of [[6.6, 0, 1.6, 0.7, 0.45, 0.5], [6.65, 0.45, 1.6, 0.5, 0.35, 0.4], [6.5, 0, 2.3, 0.5, 0.3, 0.45], [6.62, 0.8, 1.62, 0.32, 0.24, 0.3]]) {
    const pk = new THREE.Group(); pk.add(rbox(w2, h2, d2, 0.02, mat('#c99a6a', { rough: 0.95 }), 1).translateY(h2 / 2));
    pk.add(wbox(-w2 / 2 - 0.005, 0, -0.012, w2 / 2 + 0.005, h2 + 0.005, 0.012, mat('#7a3b30')), wbox(-0.012, 0, -d2 / 2 - 0.005, 0.012, h2 + 0.005, d2 / 2 + 0.005, mat('#7a3b30')));
    pk.position.set(x, y, z); pk.rotation.y = x * 3; g.add(pk);
  }
  // a spinning rack of picture postcards
  const spin = new THREE.Group();
  spin.add(cyl(0.25, 0.3, 0.05, mat('#3a3a40', { metal: 0.5 }), 14).translateY(0.025), cyl(0.025, 0.025, 1.7, mat('#3a3a40', { metal: 0.5 }), 6).translateY(0.85));
  for (let k = 0; k < 4; k++) for (let j = 0; j < 3; j++) { const c = new THREE.Mesh(new THREE.PlaneGeometry(0.3, 0.22), mat(['#ffe3ef', '#e3f1ff', '#fff3c4', '#e3ffe9'][(k + j) % 4], { rough: 0.9, side: THREE.DoubleSide })); const a = (k / 4) * Math.PI * 2; c.position.set(Math.sin(a) * 0.2, 0.8 + j * 0.3, Math.cos(a) * 0.2); c.rotation.y = a; spin.add(c); }
  spin.position.set(3.2, 0, 6.2); g.add(spin);
  g.add(at(F.rug({ w: 1.6, d: 4.4, field: '#2f4a63', border: '#c8312f', accent: '#f2c14e' } as any), 9.8, 0.03, 5.2));
  const loader = new THREE.TextureLoader();
  let cardsKey = '';
  g.add(at(F.plant({ kind: 'monstera', scale: 1.6, seed: 4 } as any), 0.7, 0, 0.8));
  g.add(at(F.bench({ color: '#7a3b30', cushion: '#f2e2c6' } as any), 4.0, 0, 9.0));
  const lamp = warmLight('#ffd9a0', 6, 9.8, 3.2, 3.0, 9);
  return {
    avoid: [{ x: 7.2, y: 0, w: 5.2, d: 2.5 }, { x: 11.1, y: 7.9, w: 1.0, d: 1.0 }, { x: 0.4, y: 6.6, w: 2.0, d: 1.6 }, { x: 3.4, y: 8.7, w: 1.2, d: 0.6 }, { x: 6.1, y: 1.2, w: 1.0, d: 1.5 }, { x: 2.85, y: 5.85, w: 0.7, d: 0.7 }],
    starts: [[9.8, 3.4], [5.0, 3.0], [4.4, 4.6], [6.4, 6.2], [9.0, 6.6], [4.6, 7.6], [2.4, 2.8]],
    lights: [lamp],
    update: (d) => {
      const cards: any[] = (d.postcards || []).slice(0, 12);
      const k = cards.map((c) => c.slug + c.at).join(',');
      if (k === cardsKey) return; cardsKey = k;
      cardsG.clear();
      const n = Math.max(cards.length, 1);
      const cw = 1.3, ch = 0.95;
      const perRow = Math.min(4, n), rows = Math.ceil(Math.min(n, 12) / 4);
      (cards.length ? cards : [null]).forEach((c, i) => {
        const r = Math.floor(i / 4), k2 = i % 4, inRow = Math.min(perRow, n - r * 4);
        const x = 3.7 + (k2 - (inRow - 1) / 2) * 1.55, y = 2.65 + ((rows - 1) / 2 - r) * 1.12 + (rows === 1 ? 0 : 0);
        const m = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.85 });
        if (c) loader.load(c.image, (t) => { t.colorSpace = THREE.SRGBColorSpace; m.map = t; m.needsUpdate = true; });
        else m.map = letters([{ text: 'No postcards yet today', size: 34, color: '#6e4b33' }, { text: 'postcard <words> sends one', size: 24, color: '#8a7460' }], 400, 290, '#f7f1e3');
        const card = new THREE.Mesh(new THREE.PlaneGeometry(cw, ch), m);
        card.position.set(x, y, 0.09); card.rotation.z = ((i * 37) % 7 - 3) * 0.018; cardsG.add(card);
        const border = new THREE.Mesh(new THREE.PlaneGeometry(cw + 0.08, ch + 0.08), mat('#fffaf0', { rough: 0.9 }));
        border.position.set(x, y, 0.085); border.rotation.z = card.rotation.z; cardsG.add(border);
        const pin = sphere(0.045, mat(['#e8739c', '#7ec8ff', '#ffd36b'][i % 3], { rough: 0.3 }), 8, 6); pin.position.set(x, y + ch / 2 - 0.06, 0.12); cardsG.add(pin);
      });
    },
  };
}

function printShop(g: THREE.Group): Built {
  const iron = mat('#4a4048', { rough: 0.45, metal: 0.5 }), red = mat('#b8332f', { rough: 0.4, metal: 0.3 }), brass = mat('#d6b05a', { rough: 0.3, metal: 0.7 });
  // the press: a heavy base, two cheeks, a platen and a big wheel to turn
  const press = new THREE.Group();
  press.add(rbox(2.4, 0.9, 1.2, 0.05, iron, 2).translateY(0.45));
  for (const x of [-0.95, 0.95]) press.add(at(rbox(0.22, 2.0, 0.4, 0.04, red, 2), x, 1.9, 0));
  press.add(at(rbox(2.2, 0.3, 0.5, 0.05, red, 2), 0, 2.95, 0));
  press.add(at(rbox(1.5, 0.12, 0.9, 0.03, iron, 1), 0, 1.5, 0));
  press.add(at(cyl(0.06, 0.06, 1.3, brass, 10), 0, 2.2, 0));
  const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.6, 0.05, 8, 28), iron);
  wheel.rotation.y = Math.PI / 2; wheel.position.set(1.35, 1.8, 0); press.add(wheel);
  for (let i = 0; i < 4; i++) { const s = cyl(0.025, 0.025, 1.2, iron, 6); s.rotation.x = (i / 4) * Math.PI; s.position.set(1.35, 1.8, 0); press.add(s); }
  press.add(at(cyl(0.08, 0.08, 0.25, brass, 10).rotateZ(Math.PI / 2), 1.27, 1.8, 0));
  press.add(at(new THREE.Mesh(new THREE.PlaneGeometry(1.0, 0.7), mat('#ffffff', { map: sheet(1), rough: 0.9 })).rotateX(-Math.PI / 2), 0, 0.92, 0.1));
  press.position.set(7.0, 0, 3.0); g.add(press);
  // the ink slab and rollers
  g.add(wbox(10.0, 0, 4.0, 11.6, 0.9, 4.9, mat('#6e4430')), wbox(9.95, 0.9, 3.95, 11.65, 0.95, 4.95, mat('#3a3a40', { rough: 0.2 })));
  g.add(at(cyl(0.08, 0.08, 0.5, mat('#1c1414', { rough: 0.2 }), 12).rotateZ(Math.PI / 2), 10.6, 1.03, 4.4));
  const ink = new THREE.Mesh(new THREE.CircleGeometry(0.35, 18), mat('#1d2a4a', { rough: 0.15 })); ink.rotation.x = -Math.PI / 2; ink.position.set(11.1, 0.955, 4.5); g.add(ink);
  // a table of today's run, tied in bundles for the round
  g.add(wbox(4.6, 0.72, 6.4, 8.4, 0.78, 7.3, mat('#a0704a', { rough: 0.6 })));
  for (const [x, z] of [[4.75, 6.55], [8.25, 6.55], [4.75, 7.15], [8.25, 7.15]]) g.add(at(cyl(0.04, 0.04, 0.72, mat('#6e4430'), 8), x, 0.36, z));
  for (let i = 0; i < 4; i++) for (let k = 0; k < 3 + (i % 2); k++) { const p = rbox(0.55, 0.07, 0.4, 0.01, mat(k % 2 ? '#f7f1e3' : '#efe6d2'), 1); p.position.set(5.1 + i * 0.9, 0.82 + k * 0.075, 6.85); p.rotation.y = k * 0.06; g.add(p); }
  // cases of type: a cabinet of shallow drawers
  const cab = new THREE.Group(), cw = mat('#8a5a3a', { rough: 0.6 });
  cab.add(rbox(1.8, 1.1, 0.7, 0.02, cw, 1).translateY(0.55));
  for (let i = 0; i < 6; i++) { cab.add(at(rbox(1.66, 0.15, 0.02, 0.01, mat('#a0704a'), 1), 0, 0.12 + i * 0.17, 0.36)); cab.add(at(cyl(0.025, 0.025, 0.03, brass, 8).rotateX(Math.PI / 2), 0, 0.2 + i * 0.17, 0.38)); }
  const slope = rbox(1.8, 0.06, 0.8, 0.01, mat('#6e4430'), 1); slope.position.set(0, 1.2, 0.05); slope.rotation.x = 0.35; cab.add(slope);
  for (let i = 0; i < 40; i++) { const t = rbox(0.03, 0.03, 0.03, 0.005, mat('#9a9aa0', { metal: 0.6, rough: 0.4 }), 1); t.position.set(-0.8 + (i % 20) * 0.08, 1.26 - Math.floor(i / 20) * 0.12, 0.1 + Math.floor(i / 20) * 0.3); cab.add(t); }
  cab.position.set(10.4, 0, 0.6); g.add(cab);
  // the drying lines along the left of the room, sheets pegged along them; one carries today's headline
  const lineM = new THREE.LineBasicMaterial({ color: '#8a7460' });
  const sheets = new THREE.Group(); g.add(sheets);
  const headM = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.9, side: THREE.DoubleSide, map: sheet(0, 'A quiet day so far') });
  const head = new THREE.Mesh(new THREE.PlaneGeometry(0.95, 1.3), headM); head.userData.keep = true;
  for (const [x, y] of [[1.7, 3.55], [3.2, 3.65]] as [number, number][]) {
    const pts = []; for (let i = 0; i <= 20; i++) { const t = i / 20; pts.push(new THREE.Vector3(x, y - Math.sin(Math.PI * t) * 0.22, 0.1 + t * 9.6)); }
    g.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), lineM));
    for (let i = 0; i < 6; i++) {
      const z = 1.0 + i * 1.55, t = (z - 0.1) / 9.6, yy = y - Math.sin(Math.PI * t) * 0.22;
      const s2 = (x === 3.2 && i === 4) ? head : new THREE.Mesh(new THREE.PlaneGeometry(0.75, 1.0), mat('#ffffff', { map: sheet(i + x), rough: 0.9, side: THREE.DoubleSide }));
      const h = (s2 === head ? 1.3 : 1.0);
      s2.position.set(x, yy - h / 2 - 0.02, z); s2.rotation.y = Math.PI / 2 - 0.15; sheets.add(s2);
      const peg = rbox(0.04, 0.14, 0.05, 0.01, mat('#c9a06a'), 1); peg.position.set(x, yy - 0.02, z); sheets.add(peg);
    }
  }
  // stacks of the paper, tied with string
  for (const [x, z, n] of [[1.2, 1.2, 6], [1.9, 1.0, 4], [11.6, 8.6, 5]]) for (let i = 0; i < n; i++) { const p = rbox(0.6, 0.06, 0.45, 0.01, mat(i % 2 ? '#f7f1e3' : '#efe6d2'), 1); p.position.set(x, 0.03 + i * 0.065, z); p.rotation.y = i * 0.07; g.add(p); }
  const board = new Board(2.6, 0.9, [520, 180], '#1c1620', 0);
  board.mesh.position.set(10.4, 2.85, 0.06); g.add(board.mesh);
  g.add(wbox(9.0, 2.33, 0.02, 11.8, 3.37, 0.05, mat('#9b2b2b')));
  g.add(at(F.plant({ kind: 'snake', scale: 1.6, seed: 2 } as any), 12.3, 0, 0.8));
  const lamp = warmLight('#ffe2b0', 6, 7.0, 3.4, 4.4, 9);
  const pend = F.pendant({ color: '#2f4a3b', drop: 0.7, top: WALL0 } as any); pend.position.set(7.0, 0, 4.2); g.add(pend);
  return {
    avoid: [{ x: 5.4, y: 2.1, w: 3.4, d: 1.8 }, { x: 9.9, y: 3.9, w: 1.8, d: 1.1 }, { x: 9.4, y: 0.2, w: 2.0, d: 1.2 }, { x: 0.8, y: 0.7, w: 1.5, d: 0.9 }, { x: 11.2, y: 8.3, w: 0.8, d: 0.7 }, { x: 4.5, y: 6.3, w: 4.0, d: 1.1 }],
    starts: [[6.8, 5.2], [9.0, 2.0], [4.2, 4.2], [2.4, 6.4], [6.4, 8.4], [9.8, 7.8], [8.8, 5.8]],
    lights: [lamp],
    update: (d) => {
      const hl = d.front?.headline || 'A quiet day so far';
      if (headM.userData.k !== hl) { headM.userData.k = hl; headM.map?.dispose(); headM.map = sheet(0, hl); headM.needsUpdate = true; }
      board.set([{ text: `SETTING DAY ${d.day}`, size: 40, color: '#ffd97a' }, { text: d.front?.headline || 'a quiet day so far', size: 30, color: '#fff3c8' }]);
    },
    tick: (_dt, t) => { wheel.rotation.x = Math.sin(t * 0.6) * 0.4; },
  };
}

function marketHall(g: THREE.Group): Built {
  const beam = mat('#7a4a2e', { rough: 0.6 });
  // the timber frame: posts and a long beam, a clock over the gable end
  for (const x of [0.4, 6.5, 12.6]) g.add(wbox(x - 0.12, 0, 0.3, x + 0.12, WALL0 - 0.1, 0.54, beam));
  g.add(wbox(0, WALL0 - 0.5, 0.25, W0, WALL0 - 0.28, 0.6, beam));
  const clock = new THREE.Group();
  clock.add(cyl(0.55, 0.55, 0.1, mat('#6e4430'), 28).rotateX(Math.PI / 2));
  clock.add(at(cyl(0.47, 0.47, 0.02, mat('#f7f1e3', { emissive: '#fff3d6', emissiveIntensity: 0.15 }), 28).rotateX(Math.PI / 2), 0, 0, 0.06));
  const hand = (l: number, a: number) => { const h = rbox(0.03, l, 0.01, 0.005, mat('#1c1414'), 1); h.geometry.translate(0, l / 2, 0); h.rotation.z = a; h.position.z = 0.08; clock.add(h); return h; };
  const hh = hand(0.25, 0), mh = hand(0.38, 0);
  clock.position.set(6.5, 2.9, 0.62); g.add(clock);
  // stalls: a counter, an awning and goods; boards with the sellers' names
  const AW = [['#c8553d', '#f3e6cc'], ['#4f8a8b', '#f3e6cc'], ['#e9b44c', '#fffaf0'], ['#8e5aa8', '#f3e6cc']];
  const stalls: { board: Board; goods: THREE.Group }[] = [];
  const spots: [number, number, number][] = [[2.2, 1.6, 0], [5.6, 1.6, 0], [9.0, 1.6, 0], [1.4, 6.2, Math.PI / 2]];
  spots.forEach(([x, z, ry], i) => {
    const s = new THREE.Group();
    s.add(wbox(-1.3, 0, -0.4, 1.3, 0.9, 0.4, mat('#a0704a', { rough: 0.6 })), wbox(-1.36, 0.9, -0.46, 1.36, 0.96, 0.46, mat('#e8d6b4', { rough: 0.5 })));
    for (const px of [-1.25, 1.25]) s.add(wbox(px - 0.05, 0, -0.45, px + 0.05, 2.3, -0.35, beam));
    const aw = awning(2.8, 1.1, AW[i][0], AW[i][1]); aw.position.set(0, 2.35, 0.05); s.add(aw);
    const b = new Board(1.6, 0.36, [400, 90], '#3a2a20'); b.mesh.position.set(0, 1.95, -0.34); s.add(b.mesh);
    const goods = new THREE.Group(); goods.userData.keep = true; s.add(goods);
    // a basket and a jug at the stall's end, whatever's on sale
    const bk = lathe([[0, 0], [0.18, 0], [0.24, 0.16], [0.25, 0.18], [0, 0.18]], mat('#c9a06a', { rough: 0.9 }), 14); bk.position.set(1.05, 0.96, -0.15); s.add(bk);
    const jug = lathe([[0, 0], [0.1, 0], [0.13, 0.12], [0.07, 0.26], [0.08, 0.3], [0, 0.3]], mat(['#5f8b9a', '#c8553d', '#e9b44c', '#8e5aa8'][i], { rough: 0.4 }), 14); jug.position.set(-1.05, 0.96, -0.2); s.add(jug);
    s.position.set(x, 0, z); s.rotation.y = ry; g.add(s);
    stalls.push({ board: b, goods });
  });
  // the season's limited piece on a stand, under a glass dome
  const stand = new THREE.Group();
  stand.add(cyl(0.4, 0.45, 1.0, mat('#f3e6cc', { rough: 0.5 }), 20).translateY(0.5));
  stand.add(at(lathe([[0, 0], [0.38, 0], [0.38, 0.5], [0.3, 0.7], [0.15, 0.8], [0, 0.82]], mat('#dff3ff', { opacity: 0.28, rough: 0.05 }), 24), 0, 1.0, 0));
  const piece = new THREE.Mesh(new THREE.IcosahedronGeometry(0.2, 1), new THREE.MeshStandardMaterial({ color: '#ffd36b', emissive: '#ffb84a', emissiveIntensity: 0.8, roughness: 0.3, flatShading: true }));
  piece.position.y = 1.3; piece.userData.keep = true; stand.add(piece);
  const tag = new Board(1.4, 0.4, [420, 120], '#fffaf0'); tag.mesh.position.set(0, 0.62, 0.46); stand.add(tag.mesh);
  stand.position.set(9.6, 0, 6.4); g.add(stand);
  // crates and barrels about the floor, and bunting
  for (const [x, z, c] of [[11.8, 3.8, '#c94a3a'], [12.0, 4.5, '#e2a23b'], [0.9, 9.0, '#7fa45a']] as [number, number, string][]) g.add(at(crate(c, 0.6), x, 0, z, x));
  const barrel = lathe([[0, 0], [0.3, 0], [0.36, 0.35], [0.3, 0.7], [0, 0.7]], mat('#8a5a3a', { rough: 0.7 }), 16); barrel.position.set(12.1, 0, 8.6); g.add(barrel);
  const flags = new THREE.Group();
  for (let i = 0; i < 18; i++) { const t = i / 17; const f = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.3, 3), mat(['#c8553d', '#e9b44c', '#4f8a8b', '#8e5aa8'][i % 4], { rough: 0.8 })); f.rotation.x = Math.PI; f.position.set(0.6 + t * 11.8, WALL0 - 0.8 - Math.sin(Math.PI * t) * 0.4, 0.7 + t * 0.1); flags.add(f); }
  g.add(flags);
  const lamp = warmLight('#ffe0b0', 5, 6.4, 3.4, 4.0, 10);
  return {
    avoid: [{ x: 0.8, y: 1.1, w: 2.8, d: 1.0 }, { x: 4.2, y: 1.1, w: 2.8, d: 1.0 }, { x: 7.6, y: 1.1, w: 2.8, d: 1.0 }, { x: 0.9, y: 4.8, w: 1.0, d: 2.8 }, { x: 9.1, y: 5.9, w: 1.0, d: 1.0 }, { x: 11.4, y: 3.4, w: 1.1, d: 1.6 }, { x: 11.7, y: 8.2, w: 0.8, d: 0.8 }],
    starts: [[3.8, 3.8], [7.4, 3.6], [10.2, 3.2], [3.2, 6.4], [6.4, 6.8], [8.0, 8.4], [4.4, 8.6]],
    lights: [lamp],
    update: (d) => {
      const st: any[] = d.stalls || [];
      stalls.forEach((s, i) => {
        const x = st[i];
        s.board.set(x ? [{ text: `${x.name}'s stall`, size: 44, color: '#fbe9c8' }] : [{ text: 'free stall', size: 40, color: '#b8a58c' }]);
        const k = x ? x.items.length : 0;
        if (s.goods.userData.k === k) return; s.goods.userData.k = k; s.goods.clear();
        for (let j = 0; j < Math.min(k, 5); j++) { const c = crate(['#c94a3a', '#e2a23b', '#7fa45a', '#7ec8ff', '#c59bff'][(i + j) % 5], 0.42); c.position.set(-1.0 + j * 0.5, 0.96, 0); s.goods.add(c); }
      });
      tag.set(d.limited ? [{ text: d.limited.name, size: 44, color: '#6e2440' }, { text: 'this season only', size: 30, color: '#8a7460' }] : [{ text: 'between seasons', size: 40, color: '#8a7460' }]);
      piece.visible = !!d.limited;
    },
    tick: (dt, t) => { piece.rotation.y += dt * 0.6; piece.position.y = 1.3 + Math.sin(t * 1.4) * 0.03; const now = new Date(); mh.rotation.z = -(now.getMinutes() / 60) * Math.PI * 2; hh.rotation.z = -((now.getHours() % 12) / 12 + now.getMinutes() / 720) * Math.PI * 2; },
  };
}

function workshop(g: THREE.Group): Built {
  const top = mat('#c99862', { rough: 0.6 }), leg = mat('#7a4a2e', { rough: 0.6 }), iron = mat('#3a3a40', { rough: 0.4, metal: 0.6 });
  const bench = (x: number, z: number, w: number, ry = 0) => {
    const b = new THREE.Group();
    b.add(wbox(-w / 2, 0.82, -0.45, w / 2, 0.92, 0.45, top));
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.add(wbox(sx * (w / 2 - 0.15) - 0.06, 0, sz * 0.35 - 0.06, sx * (w / 2 - 0.15) + 0.06, 0.82, sz * 0.35 + 0.06, leg));
    b.add(wbox(-w / 2 + 0.15, 0.2, -0.38, w / 2 - 0.15, 0.25, 0.38, leg));
    // a vice at one end
    b.add(wbox(w / 2 - 0.35, 0.92, 0.3, w / 2 - 0.05, 1.12, 0.5, iron), at(cyl(0.02, 0.02, 0.4, iron, 6).rotateX(Math.PI / 2), w / 2 - 0.2, 1.0, 0.62));
    b.position.set(x, 0, z); b.rotation.y = ry; return b;
  };
  g.add(bench(4.2, 1.2, 4.6), bench(9.8, 1.2, 4.2), bench(6.6, 5.4, 5.0));
  // pegboard of tools over the back benches
  g.add(wbox(2.0, 1.4, 0.02, 12.0, 3.6, 0.08, mat('#d9c09a', { rough: 0.9 })));
  const tools = mat('#55606a', { rough: 0.4, metal: 0.6 }), handle = mat('#b5654b', { rough: 0.6 });
  for (let i = 0; i < 14; i++) {
    const x = 2.5 + i * 0.68, y = 2.4 + (i % 2) * 0.5;
    if (i % 3 === 0) { g.add(wbox(x - 0.04, y - 0.4, 0.09, x + 0.04, y, 0.12, handle), wbox(x - 0.16, y, 0.09, x + 0.16, y + 0.1, 0.13, tools)); } // a hammer
    else if (i % 3 === 1) { g.add(wbox(x - 0.25, y - 0.1, 0.09, x + 0.25, y + 0.1, 0.1, tools), wbox(x + 0.25, y - 0.08, 0.09, x + 0.45, y + 0.08, 0.13, handle)); } // a saw
    else { g.add(wbox(x - 0.03, y - 0.45, 0.09, x + 0.03, y, 0.11, tools), wbox(x - 0.05, y, 0.09, x + 0.05, y + 0.18, 0.13, handle)); } // a screwdriver
  }
  // the lumber rack on the left wall, planks across it
  for (const z of [1.4, 3.8]) g.add(wbox(0.06, 0.4, z - 0.05, 0.6, 0.48, z + 0.05, leg), wbox(0.06, 1.2, z - 0.05, 0.6, 1.28, z + 0.05, leg));
  for (let i = 0; i < 5; i++) g.add(wbox(0.12 + (i % 3) * 0.14, 0.48 + Math.floor(i / 3) * 0.8, 0.9, 0.24 + (i % 3) * 0.14, 0.56 + Math.floor(i / 3) * 0.8, 4.4, mat(['#d9b07a', '#c99862', '#e2c48f'][i % 3], { rough: 0.7 })));
  // sawdust and shavings on the floor
  const dust = mat('#e8d2a6', { rough: 1 });
  for (const [x, z, r] of [[4.0, 2.2, 0.7], [9.6, 2.3, 0.6], [6.8, 6.4, 0.8]]) { const d = new THREE.Mesh(new THREE.CircleGeometry(r, 18), dust); d.rotation.x = -Math.PI / 2; d.position.set(x, 0.025, z); d.userData.noShadow = true; g.add(d); }
  // the tally board on the left wall, and a shelf of things made today
  const board = new Board(2.0, 1.2, [400, 240], '#2f4a3b');
  board.mesh.position.set(0.15, 2.75, 5.9); board.mesh.rotation.y = Math.PI / 2; g.add(board.mesh);
  g.add(wbox(0.02, 2.05, 4.8, 0.12, 3.45, 7.0, mat('#8a5a3a')));
  g.add(wbox(0.02, 1.25, 4.8, 0.6, 1.31, 7.0, leg), wbox(0.02, 0.55, 4.8, 0.6, 0.61, 7.0, leg));
  for (const z of [4.85, 6.95]) g.add(wbox(0.02, 0, z - 0.03, 0.6, 1.31, z + 0.03, leg));
  const made = new THREE.Group(); made.userData.keep = true; g.add(made);
  // a stool or two
  for (const [x, z] of [[4.2, 2.3], [9.8, 2.3], [5.4, 6.5], [7.8, 6.5]]) { const s = new THREE.Group(); s.add(cyl(0.22, 0.22, 0.06, top, 16).translateY(0.62)); for (let k = 0; k < 3; k++) { const l = cyl(0.025, 0.03, 0.62, leg, 6); const a = (k / 3) * Math.PI * 2; l.position.set(Math.cos(a) * 0.14, 0.31, Math.sin(a) * 0.14); l.rotation.set(Math.sin(a) * 0.15, 0, -Math.cos(a) * 0.15); s.add(l); } s.position.set(x, 0, z); g.add(s); }
  // a sawhorse with a plank across it, and a toolbox
  const horse = new THREE.Group();
  for (const sx of [-0.5, 0.5]) for (const a of [-0.25, 0.25]) { const l = rbox(0.07, 0.75, 0.07, 0.01, leg, 1); l.position.set(sx, 0.36, a * 0.6); l.rotation.x = a; horse.add(l); }
  horse.add(wbox(-0.65, 0.68, -0.05, 0.65, 0.76, 0.05, leg));
  const pl = wbox(-1.1, 0.76, -0.14, 1.1, 0.82, 0.14, mat('#e2c48f', { rough: 0.7 })); pl.rotation.y = 0.15; horse.add(pl);
  horse.position.set(9.6, 0, 7.0); horse.rotation.y = 0.4; g.add(horse);
  const tb = new THREE.Group();
  tb.add(rbox(0.6, 0.3, 0.3, 0.03, mat('#c8553d', { rough: 0.4 }), 2).translateY(0.15));
  tb.add(at(new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.02, 6, 12, Math.PI), iron), 0, 0.3, 0));
  tb.position.set(10.9, 0, 7.6); tb.rotation.y = -0.3; g.add(tb);
  const radio = rbox(0.5, 0.32, 0.22, 0.05, mat('#7ec8ff', { rough: 0.4 }), 2); radio.position.set(11.4, 1.08, 1.0); g.add(radio);
  const lamp = warmLight('#fff0d0', 6, 6.6, 3.6, 3.4, 10);
  for (const x of [4.2, 9.8]) { const p = F.pendant({ color: '#c8553d', drop: 0.9, top: WALL0 } as any); p.position.set(x, 0, 1.2); g.add(p); }
  return {
    avoid: [{ x: 1.8, y: 0.7, w: 4.8, d: 1.0 }, { x: 7.6, y: 0.7, w: 4.4, d: 1.0 }, { x: 4.0, y: 4.9, w: 5.2, d: 1.0 }, { x: 0, y: 0.8, w: 0.7, d: 3.7 }, { x: 0, y: 4.7, w: 0.7, d: 2.4 }, { x: 8.6, y: 6.3, w: 2.0, d: 1.4 }, { x: 10.5, y: 7.3, w: 0.8, d: 0.6 }],
    starts: [[4.2, 2.6], [9.8, 2.6], [5.6, 4.2], [8.0, 6.8], [3.0, 7.6], [10.8, 5.6], [6.4, 8.4]],
    lights: [lamp],
    update: (d) => {
      const n = (d.made || []).length;
      board.set([{ text: 'MADE TODAY', size: 32, color: '#f2c14e' }, { text: `${n}`, size: 80, color: '#f6f2e8' }, { text: d.together ? `${d.together} made together` : (d.benches?.length ? `${d.benches.length} at the benches` : 'the benches are free'), size: 28, color: '#cfd8cf' }]);
      if (made.userData.k === n) return; made.userData.k = n; made.clear();
      for (let i = 0; i < Math.min(n, 10); i++) {
        const p = new THREE.Group(), c = ['#e8739c', '#7ec8ff', '#ffd36b', '#7ee8c6', '#c59bff'][i % 5];
        p.add(rbox(0.32, 0.26, 0.3, 0.03, mat('#d9b98a', { rough: 0.9 }), 1).translateY(0.13));
        p.add(wbox(-0.17, 0, -0.03, 0.17, 0.28, 0.03, mat(c)), wbox(-0.03, 0, -0.16, 0.03, 0.28, 0.16, mat(c)));
        p.add(at(sphere(0.06, mat(c), 8, 6), 0, 0.3, 0));
        p.position.set(0.32, i < 5 ? 1.31 : 0.61, 5.15 + (i % 5) * 0.42); p.rotation.y = i; made.add(p);
      }
    },
  };
}

function cafe(g: THREE.Group): Built {
  // the oven: a round-bellied brick oven, glowing, in the back corner
  const brick = surf({ kind: 'brick', color: '#b5654b' });
  const oven = new THREE.Group();
  oven.add(wbox(-1.2, 0, -1.0, 1.2, 0.9, 1.0, brick));
  const dome = sphere(1.05, brick, 24, 12); dome.scale.set(1, 0.85, 0.9); dome.position.y = 0.9; oven.add(dome);
  const mouth = new THREE.Mesh(new THREE.CircleGeometry(0.42, 20, 0, Math.PI), new THREE.MeshBasicMaterial({ color: '#ffae5c', toneMapped: false }));
  mouth.position.set(0, 0.95, 0.93); mouth.userData.keep = true; mouth.userData.noShadow = true; oven.add(mouth);
  oven.add(at(cyl(0.18, 0.2, 1.6, brick, 12), 0.5, 2.2, -0.5));
  oven.position.set(1.6, 0, 1.4); g.add(oven);
  // the counter of cakes under glass, and a chalkboard menu
  const cw = mat('#f3e6cc', { rough: 0.5 }), ct = mat('#8a4f36', { rough: 0.4 });
  g.add(wbox(4.0, 0, 0.6, 9.4, 1.0, 1.5, cw), wbox(3.9, 1.0, 0.5, 9.5, 1.07, 1.6, ct));
  for (let x = 4.2; x < 9.3; x += 0.45) g.add(wbox(x, 0.1, 1.5, x + 0.22, 0.9, 1.53, mat('#f4c7d4', { rough: 0.6 })));
  const cakes = ['#f4a7c2', '#ffe08a', '#c59bff', '#ffffff', '#9fe3d0'];
  for (let i = 0; i < 5; i++) {
    const x = 4.6 + i * 1.1;
    g.add(at(cyl(0.3, 0.3, 0.03, mat('#e8e0d0', { rough: 0.3 }), 18), x, 1.09, 1.05), at(cyl(0.04, 0.06, 0.1, mat('#e8e0d0', { rough: 0.3 }), 10), x, 1.13, 1.05));
    const cake = new THREE.Group();
    cake.add(cyl(0.22, 0.22, 0.2, mat(cakes[i], { rough: 0.6 }), 20).translateY(0.1));
    cake.add(cyl(0.225, 0.225, 0.04, mat('#fffaf0', { rough: 0.5 }), 20).translateY(0.2));
    for (let k = 0; k < 6; k++) { const b = sphere(0.035, mat('#e23a4a', { rough: 0.3 }), 8, 6); const a = (k / 6) * Math.PI * 2; b.position.set(Math.cos(a) * 0.15, 0.25, Math.sin(a) * 0.15); cake.add(b); }
    cake.position.set(x, 1.19, 1.05); g.add(cake);
    g.add(at(lathe([[0, 0], [0.3, 0], [0.3, 0.3], [0.2, 0.42], [0.04, 0.46], [0.05, 0.52], [0, 0.53]], mat('#e9f6ff', { opacity: 0.25, rough: 0.05 }), 20), x, 1.17, 1.05));
  }
  const menu = new Board(2.2, 1.5, [440, 300], '#26302b');
  menu.mesh.position.set(10.9, 2.6, 0.09); g.add(menu.mesh);
  g.add(wbox(9.7, 1.75, 0.02, 12.1, 3.45, 0.08, mat('#8a5a3a')));
  // little tables with checked cloths, and chairs
  const check = canvasTex(64, 64, (c) => { c.fillStyle = '#fffaf0'; c.fillRect(0, 0, 64, 64); c.fillStyle = 'rgba(200,85,61,.55)'; for (let i = 0; i < 8; i += 2) { c.fillRect(i * 8, 0, 8, 64); c.fillRect(0, i * 8, 64, 8); } });
  check.wrapS = check.wrapT = THREE.RepeatWrapping; check.repeat.set(3, 3);
  const clothM = mat('#ffffff', { map: check, rough: 0.9 });
  const seats: { at: P; face: number }[] = [];
  const TABLES: P[] = [[4.4, 4.6], [8.4, 4.4], [4.0, 7.8], [8.0, 7.8], [11.4, 6.2]];
  TABLES.forEach(([x, z], i) => {
    const t = new THREE.Group();
    t.add(cyl(0.6, 0.6, 0.04, mat('#7a4a2e'), 24).translateY(0.74), cyl(0.06, 0.09, 0.72, mat('#3a3a40', { metal: 0.5 }), 10).translateY(0.36), cyl(0.3, 0.3, 0.04, mat('#3a3a40', { metal: 0.5 }), 16).translateY(0.02));
    t.add(at(cyl(0.68, 0.64, 0.12, clothM, 24, 1), 0, 0.71, 0));
    t.add(at(cyl(0.05, 0.04, 0.16, mat('#e8e0d0', { rough: 0.3 }), 10), 0, 0.84, 0), at(sphere(0.07, mat(['#ff9bd2', '#ffd36b', '#9be8ff'][i % 3]), 8, 6), 0, 0.98, 0));
    for (const a of [0.3, 0.3 + Math.PI]) { const c = F.chair({ wood: 'walnut', seat: '#c8553d' } as any); c.position.set(Math.cos(a) * 0.9, 0, Math.sin(a) * 0.9); c.rotation.y = -a - Math.PI / 2; t.add(c); seats.push({ at: [x + Math.cos(a) * 0.9, z + Math.sin(a) * 0.9], face: a + Math.PI }); }
    t.position.set(x, 0, z); g.add(t);
  });
  for (const x of [4.4, 8.4]) { const p = F.pendant({ color: '#f4a7c2', drop: 1.2, top: WALL0 } as any); p.position.set(x, 0, 4.5); g.add(p); }
  g.add(at(F.plant({ kind: 'monstera', scale: 1.5, seed: 7 } as any), 12.3, 0, 0.8), at(F.plant({ kind: 'fern', scale: 1.4, seed: 3 } as any), 0.7, 0, 9.2));
  const ovenL = warmLight('#ff9a50', 6, 1.6, 1.0, 2.6, 6);
  const room = warmLight('#ffe0c0', 5, 6.4, 3.4, 4.4, 10);
  return {
    avoid: [{ x: 0.3, y: 0.3, w: 2.6, d: 2.2 }, { x: 3.8, y: 0.4, w: 5.8, d: 1.3 }, ...TABLES.map(([x, z]) => ({ x: x - 1.15, y: z - 0.75, w: 2.3, d: 1.5 }))],
    starts: [[6.4, 2.6], [2.4, 4.4], [6.2, 6.2], [10.2, 3.4], [2.2, 6.4], [10.0, 8.8], [6.0, 9.0]],
    seats, lights: [ovenL, room],
    update: (d) => {
      const dishes: any[] = d.dishes || [];
      const names = [...new Set(dishes.map((x) => x.dish))].slice(0, 4);
      menu.set([{ text: 'FROM THE OVENS TODAY', size: 28, color: '#f2c14e', font: 'Georgia, serif' }, ...(names.length ? names.map((n) => ({ text: n, size: 34, color: '#f6f2e8', font: 'Georgia, serif', weight: 600 })) : [{ text: 'the oven is warm', size: 34, color: '#cfd8cf', font: 'Georgia, serif', weight: 600 }]),
        { text: `${(d.dinners || []).length} dinner${(d.dinners || []).length === 1 ? '' : 's'} laid`, size: 24, color: '#cfd8cf' }]);
    },
    tick: (_dt, t) => { ovenL.intensity = 5 + Math.sin(t * 6.1) * 0.8 + Math.sin(t * 11.3) * 0.5; },
  };
}

function townHall(g: THREE.Group): Built {
  // a little dais at the back, a lectern, the crown's portrait over it, banners either side
  const dais = mat('#8a5a3a', { rough: 0.55 }), gold = mat('#d6b05a', { rough: 0.3, metal: 0.7 }), blue = mat('#2b3d5a', { rough: 0.7 });
  g.add(wbox(4.0, 0, 0.1, 9.0, 0.35, 2.4, dais), wbox(3.95, 0.33, 2.38, 9.05, 0.37, 2.45, gold), wbox(5.8, 0, 2.4, 7.2, 0.17, 2.85, dais));
  const lect = new THREE.Group();
  lect.add(rbox(0.7, 1.1, 0.5, 0.03, mat('#6e4430'), 1).translateY(0.55)); const lt = rbox(0.8, 0.06, 0.6, 0.02, mat('#6e4430'), 1); lt.position.y = 1.15; lt.rotation.x = 0.3; lect.add(lt);
  lect.position.set(6.5, 0.35, 1.5); g.add(lect);
  const portrait = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 2.0), mat('#ffffff', { map: crownPortrait(), rough: 0.8 }));
  portrait.position.set(6.5, 2.75, 0.1); g.add(portrait);
  g.add(wbox(5.6, 1.65, 0.02, 7.4, 3.85, 0.08, gold));
  for (const x of [4.3, 8.7]) {
    const ban = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 2.2), mat('#ffffff', { map: banner(), rough: 0.8, side: THREE.DoubleSide }));
    ban.position.set(x, 2.6, 0.1); g.add(ban);
    g.add(at(cyl(0.025, 0.025, 1.1, gold, 6).rotateZ(Math.PI / 2), x, 3.72, 0.12));
  }
  // the noticeboard on the left wall: the policy in plain words
  g.add(wbox(0.02, 1.25, 1.2, 0.14, 3.85, 4.8, mat('#6e4430')));
  g.add(wbox(0.1, 1.35, 1.3, 0.16, 3.75, 4.7, mat('#c99a6a', { rough: 1 })));
  const notice = new Board(3.2, 2.2, [640, 440], '#f7f1e3');
  notice.mesh.position.set(0.18, 2.55, 3.0); notice.mesh.rotation.y = Math.PI / 2; g.add(notice.mesh);
  for (const [y, z] of [[3.6, 1.5], [3.6, 4.5], [1.5, 1.5], [1.5, 4.5]]) g.add(at(sphere(0.05, mat('#c8312f', { rough: 0.3 }), 8, 6), 0.22, y, z));
  // the plan of the prime plots, on the left wall nearer the door
  const plan = new Board(2.0, 1.6, [400, 320], '#eef3e6');
  plan.mesh.position.set(0.1, 2.5, 6.4); plan.mesh.rotation.y = Math.PI / 2; g.add(plan.mesh);
  g.add(wbox(0.02, 1.6, 5.3, 0.08, 3.4, 7.5, mat('#6e4430')));
  // benches in rows for anyone who comes to listen
  for (const z of [4.6, 6.2, 7.8]) for (const x of [4.6, 8.4]) { const b = F.bench({ color: '#6e4430', cushion: '#c8312f' } as any); b.scale.set(1.6, 1, 1); b.position.set(x, 0, z); b.rotation.y = Math.PI; g.add(b); }
  // a red runner from the door to the dais
  g.add(at(F.rug({ w: 1.3, d: 7.0, field: '#a3243b', border: '#d6b05a', accent: '#f2c14e' } as any), 6.5, 0.03, 6.4));
  g.add(at(F.plant({ kind: 'tree', scale: 1.5, seed: 5 } as any), 11.8, 0, 0.9), at(F.plant({ kind: 'tree', scale: 1.5, seed: 6 } as any), 1.2, 0, 0.9));
  const chand = new THREE.Group();
  chand.add(at(cyl(0.01, 0.01, 0.9, gold, 4), 0, WALL0 - 0.45, 0));
  chand.add(at(new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.03, 6, 24), gold).rotateX(Math.PI / 2), 0, WALL0 - 0.95, 0));
  for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; const c = sphere(0.06, mat('#fff3d6', { emissive: '#ffd28a', emissiveIntensity: 3 }), 8, 6); c.position.set(Math.cos(a) * 0.5, WALL0 - 0.85, Math.sin(a) * 0.5); c.userData.noShadow = true; chand.add(c); }
  chand.position.set(6.5, 0, 5.5); g.add(chand);
  const lamp = warmLight('#ffe8c0', 6, 6.5, 3.2, 5.5, 10);
  const spot = warmLight('#fff0d0', 4, 6.5, 3.4, 1.4, 4);
  void blue;
  return {
    avoid: [{ x: 3.9, y: 0, w: 5.2, d: 2.9 }, { x: 0, y: 1.1, w: 0.4, d: 3.8 }, ...[4.6, 6.2, 7.8].flatMap((z) => [4.6, 8.4].map((x) => ({ x: x - 0.9, y: z - 0.25, w: 1.8, d: 0.5 }))), { x: 11.3, y: 0.4, w: 1.0, d: 1.0 }, { x: 0.7, y: 0.4, w: 1.0, d: 1.0 }],
    starts: [[2.4, 3.4], [6.5, 3.4], [10.6, 3.6], [2.4, 6.0], [10.6, 6.6], [6.5, 9.0], [2.6, 8.8]],
    lights: [lamp, spot],
    update: (d) => {
      notice.set([{ text: 'BY ORDER OF THE CROWN', size: 30, color: '#2b3d5a' }, { text: `Policy, version ${d.version}`, size: 40, color: '#1f1b1a', font: 'Georgia, serif' },
        ...(d.policy || []).slice(0, 5).map((p: string) => ({ text: p, size: 22, color: '#3a2a20', weight: 600 }))], { align: 'left', pad: 28 });
      const p = d.plots || {};
      plan.set([{ text: 'THE PLOTS', size: 30, color: '#3f6b4f' }, { text: `${p.owned ?? 0} owned`, size: 40, color: '#1f1b1a' }, { text: `${p.for_sale ?? 0} for sale`, size: 36, color: '#5a4fa8' }, { text: `${p.reserved ?? 0} held by the crown`, size: 28, color: '#6e5a48' }]);
    },
  };
}

function crownPortrait() {
  return canvasTex(256, 320, (g) => {
    const gr = g.createLinearGradient(0, 0, 0, 320); gr.addColorStop(0, '#3a4f78'); gr.addColorStop(1, '#22304d');
    g.fillStyle = gr; g.fillRect(0, 0, 256, 320);
    // a plazmi, gummy and round, with its antenna glowing and a little crown
    g.fillStyle = '#f2a37b'; g.beginPath(); g.ellipse(128, 210, 74, 80, 0, 0, 7); g.fill();
    g.fillStyle = 'rgba(255,255,255,.35)'; g.beginPath(); g.ellipse(98, 180, 18, 28, -0.4, 0, 7); g.fill();
    g.fillStyle = '#3a2a20'; for (const x of [104, 152]) { g.beginPath(); g.arc(x, 205, 7, 0, 7); g.fill(); }
    g.strokeStyle = '#3a2a20'; g.lineWidth = 4; g.beginPath(); g.arc(128, 222, 14, 0.2, Math.PI - 0.2); g.stroke();
    g.fillStyle = '#ff8fa8'; for (const x of [88, 168]) { g.beginPath(); g.ellipse(x, 226, 10, 6, 0, 0, 7); g.fill(); }
    g.strokeStyle = '#d6b05a'; g.lineWidth = 3; g.beginPath(); g.moveTo(150, 140); g.quadraticCurveTo(170, 100, 160, 80); g.stroke();
    g.fillStyle = '#9be8ff'; g.beginPath(); g.arc(160, 78, 10, 0, 7); g.fill();
    g.fillStyle = '#e9c46a'; g.beginPath(); g.moveTo(86, 144); g.lineTo(90, 108); g.lineTo(108, 128); g.lineTo(128, 100); g.lineTo(148, 128); g.lineTo(166, 108); g.lineTo(170, 144); g.closePath(); g.fill();
    g.fillStyle = '#c8312f'; for (const x of [108, 128, 148]) { g.beginPath(); g.arc(x, 136, 5, 0, 7); g.fill(); }
    g.strokeStyle = '#d6b05a'; g.lineWidth = 10; g.strokeRect(5, 5, 246, 310);
  });
}

function banner() {
  return canvasTex(128, 320, (g) => {
    g.fillStyle = '#2b3d5a'; g.fillRect(0, 0, 128, 300);
    g.beginPath(); g.moveTo(0, 290); g.lineTo(64, 320); g.lineTo(128, 290); g.fill();
    g.fillStyle = '#e9c46a'; g.beginPath(); g.moveTo(34, 150); g.lineTo(38, 110); g.lineTo(52, 130); g.lineTo(64, 100); g.lineTo(76, 130); g.lineTo(90, 110); g.lineTo(94, 150); g.closePath(); g.fill();
    g.fillRect(8, 8, 112, 4); g.fillRect(8, 280, 112, 4);
  });
}

// ---------------------------------------------------------------------------
// Plazmi University: the screening room

const PU = { W: 13, D: 11, wall: 5.0, door: [10.3, 12.5] as [number, number] };
// the seats: four rows of eight facing the screen, their middles at these z; a seat a metre wide
const ROWS = [3.6, 4.8, 6.0, 7.2], SEAT_X = [3, 4, 5, 6, 7, 8, 9, 10];
const SCREEN = { x0: 2.95, x1: 10.05, y0: 1.05, y1: 4.1, z: 0.2 };
const PJ_S = 1.25; // the projector, drawn a little larger than life so it reads
// (left of the middle, so it never stands between the page's camera and the rows)
const LENS: [number, number, number] = [3.9, 1.98 * PJ_S, 9.25];

// a course's little picture on a title card or a poster
function courseIcon(g: CanvasRenderingContext2D, course: string, x: number, y: number, s: number, ink: string) {
  g.save(); g.translate(x, y); g.strokeStyle = ink; g.fillStyle = ink; g.lineWidth = s * 0.07; g.lineJoin = 'round'; g.lineCap = 'round';
  const c = course.toLowerCase();
  if (c.includes('architect')) {
    g.beginPath(); g.moveTo(-s * 0.5, -s * 0.05); g.lineTo(0, -s * 0.5); g.lineTo(s * 0.5, -s * 0.05); g.stroke();
    g.strokeRect(-s * 0.38, -s * 0.1, s * 0.76, s * 0.55); g.strokeRect(-s * 0.1, s * 0.12, s * 0.2, s * 0.33);
    g.strokeRect(-s * 0.3, s * 0.0, s * 0.13, s * 0.13); g.strokeRect(s * 0.17, s * 0.0, s * 0.13, s * 0.13);
  } else if (c.includes('lisp')) {
    g.font = `700 ${s}px Georgia, serif`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('( )', 0, 0);
  } else if (c.includes('letter') || c.includes('neighbour')) {
    g.strokeRect(-s * 0.45, -s * 0.3, s * 0.9, s * 0.6); g.beginPath(); g.moveTo(-s * 0.45, -s * 0.3); g.lineTo(0, s * 0.05); g.lineTo(s * 0.45, -s * 0.3); g.stroke();
  } else if (c.includes('dance')) {
    g.beginPath(); g.ellipse(-s * 0.18, s * 0.28, s * 0.16, s * 0.12, -0.4, 0, 7); g.fill(); g.beginPath(); g.moveTo(-s * 0.04, s * 0.26); g.lineTo(-s * 0.04, -s * 0.4); g.lineTo(s * 0.32, -s * 0.28); g.stroke();
  } else if (c.includes('playa')) {
    g.beginPath(); g.moveTo(0, -s * 0.45); g.lineTo(0, s * 0.45); g.moveTo(-s * 0.3, -s * 0.1); g.lineTo(0, -s * 0.25); g.lineTo(s * 0.3, -s * 0.1); g.moveTo(-s * 0.25, s * 0.45); g.lineTo(0, s * 0.15); g.lineTo(s * 0.25, s * 0.45); g.stroke();
    g.beginPath(); g.arc(0, -s * 0.5, s * 0.08, 0, 7); g.fill();
  } else if (c.includes('trad')) {
    g.beginPath(); g.moveTo(0, -s * 0.45); g.lineTo(0, s * 0.4); g.moveTo(-s * 0.45, -s * 0.25); g.lineTo(s * 0.45, -s * 0.25); g.moveTo(-s * 0.25, s * 0.42); g.lineTo(s * 0.25, s * 0.42); g.stroke();
    for (const sx of [-1, 1]) { g.beginPath(); g.arc(sx * s * 0.38, -s * 0.02, s * 0.15, 0, Math.PI); g.stroke(); }
  } else {
    // gathering & making, and anything new: a hammer
    g.save(); g.rotate(-0.6); g.fillRect(-s * 0.04, -s * 0.2, s * 0.08, s * 0.65); g.fillRect(-s * 0.25, -s * 0.36, s * 0.5, s * 0.18); g.restore();
  }
  g.restore();
}

type Show = { course: string; n: number; title: string; others: string[] };
// the screen's picture: a calm title card for the course being shown, or the university's own
function screenCard(show: Show | null) {
  const w = 1280, h = 550;
  return canvasTex(w, h, (g) => {
    const gr = g.createRadialGradient(w / 2, h * 0.45, 40, w / 2, h / 2, w * 0.62);
    gr.addColorStop(0, show ? '#26405a' : '#2a3350'); gr.addColorStop(1, show ? '#122030' : '#141a2c');
    g.fillStyle = gr; g.fillRect(0, 0, w, h);
    g.strokeStyle = 'rgba(232,207,140,.75)'; g.lineWidth = 4; g.strokeRect(30, 30, w - 60, h - 60);
    g.strokeStyle = 'rgba(232,207,140,.35)'; g.lineWidth = 2; g.strokeRect(44, 44, w - 88, h - 88);
    g.textAlign = 'center'; g.textBaseline = 'middle';
    if (!show) {
      // an open book, then the name
      g.fillStyle = '#e8cf8c';
      for (const s of [-1, 1]) { g.beginPath(); g.moveTo(w / 2, 150); g.quadraticCurveTo(w / 2 + s * 38, 128, w / 2 + s * 80, 140); g.lineTo(w / 2 + s * 80, 214); g.quadraticCurveTo(w / 2 + s * 38, 204, w / 2, 224); g.closePath(); g.fill(); }
      g.fillStyle = '#9be8ff'; g.beginPath(); g.arc(w / 2, 110, 9, 0, 7); g.fill();
      g.fillStyle = '#f4ecdc'; g.font = '400 96px Georgia, "Times New Roman", serif'; g.fillText('Plazmi University', w / 2, 304);
      g.fillStyle = 'rgba(232,207,140,.9)'; g.font = '700 30px ui-rounded, system-ui, sans-serif';
      g.fillText('T H E   S C R E E N I N G   R O O M', w / 2, 384);
      g.fillStyle = 'rgba(244,236,220,.6)'; g.font = 'italic 400 30px Georgia, serif'; g.fillText('take a seat; a lesson begins when you do', w / 2, 446);
      return;
    }
    courseIcon(g, show.course, w / 2, 132, 88, '#e8cf8c');
    g.fillStyle = 'rgba(232,207,140,.9)'; g.font = '700 30px ui-rounded, system-ui, sans-serif';
    g.fillText(`L E S S O N   ${show.n}   O F   3`, w / 2, 222);
    g.fillStyle = '#f4ecdc'; g.font = '400 104px Georgia, "Times New Roman", serif'; g.fillText(show.course, w / 2, 304);
    let size = 44; g.font = `italic 400 ${size}px Georgia, serif`;
    while (g.measureText(show.title).width > w - 160 && size > 22) { size -= 2; g.font = `italic 400 ${size}px Georgia, serif`; }
    g.fillStyle = 'rgba(244,236,220,.85)'; g.fillText(show.title, w / 2, 386);
    if (show.others.length) { g.fillStyle = 'rgba(232,207,140,.7)'; g.font = '600 26px ui-rounded, system-ui, sans-serif'; g.fillText(`and, for some in the rows, ${show.others.join(' · ')}`, w / 2, 462); }
  });
}

// a theatre seat facing -z: red velvet on a walnut shell
function theatreSeat(velvet: THREE.Material, shellM: THREE.Material) {
  const g = new THREE.Group();
  g.add(at(rbox(0.8, 0.12, 0.48, 0.05, velvet, 2), 0, 0.42, -0.06));
  const back = rbox(0.8, 0.66, 0.13, 0.06, velvet, 2); back.position.set(0, 0.84, 0.24); back.rotation.x = 0.12; g.add(back);
  const sh = rbox(0.84, 0.08, 0.16, 0.03, shellM, 1); sh.position.set(0, 1.18, 0.28); sh.rotation.x = 0.12; g.add(sh);
  g.add(at(rbox(0.5, 0.3, 0.06, 0.02, shellM, 1), 0, 0.2, 0.12));
  return g;
}

// the projector on its stand: a body with a lens toward the screen (-z), two reels that turn while a lesson's on
function projector(brass: THREE.Material, reels: THREE.Object3D[]) {
  const g = new THREE.Group(), black = mat('#2a2622', { rough: 0.45, metal: 0.3 }), wood = mat('#6e4430', { rough: 0.55 });
  // a turned walnut pedestal on a marble foot
  g.add(wbox(-0.42, 0, -0.42, 0.42, 0.12, 0.42, mat('#e9e2d6', { rough: 0.5 })));
  g.add(lathe([[0.3, 0.12], [0.22, 0.3], [0.13, 0.45], [0.11, 1.5], [0.2, 1.6], [0.3, 1.66], [0, 1.66]], wood, 16));
  g.add(at(rbox(0.36, 0.42, 0.62, 0.05, black, 2), 0, 1.9, 0));
  g.add(at(cyl(0.09, 0.11, 0.3, black, 14).rotateX(Math.PI / 2), 0, 1.98, -0.42));
  const lens = cyl(0.075, 0.075, 0.02, mat('#fff4d8', { emissive: '#ffe6b0', emissiveIntensity: 2.4 }), 14); lens.rotateX(Math.PI / 2); lens.position.set(0, 1.98, -0.575); lens.userData.noShadow = true; g.add(lens);
  g.add(at(rbox(0.26, 0.2, 0.34, 0.04, black, 2), 0, 2.2, 0.06));
  g.add(at(cyl(0.05, 0.05, 0.12, brass, 8), 0, 2.36, 0.06));
  // the reels on their arms, front and back
  for (const [z, y] of [[-0.18, 2.5], [0.3, 2.42]]) {
    g.add(wbox(0.17, 2.08, z - 0.02, 0.21, y, z + 0.02, brass));
    const r = new THREE.Group();
    r.add(cyl(0.3, 0.3, 0.04, mat('#c9c4ba', { rough: 0.35, metal: 0.6 }), 24).rotateZ(Math.PI / 2));
    r.add(cyl(0.22, 0.22, 0.06, mat('#3a2e26', { rough: 0.7 }), 20).rotateZ(Math.PI / 2));
    r.add(cyl(0.05, 0.05, 0.08, brass, 10).rotateZ(Math.PI / 2));
    // holes in the reel's side, so it's seen to turn
    for (let i = 0; i < 3; i++) { const a = (i * 2 * Math.PI) / 3, h = cyl(0.045, 0.045, 0.07, mat('#6a655e', { rough: 0.5 }), 10).rotateZ(Math.PI / 2); h.position.set(0, Math.sin(a) * 0.14, Math.cos(a) * 0.14); r.add(h); }
    r.position.set(0.21, y, z); r.userData.keep = true; g.add(r); reels.push(r);
  }
  return g;
}

// the beam: soft frustums from the lens to the screen, brightest near the lens and in the middle
// (a shader of its own, as the Dance Hall's spotlights: light added, never darkening)
function beam() {
  const g = new THREE.Group();
  const lens = new THREE.Vector3(...LENS), to = new THREE.Vector3((SCREEN.x0 + SCREEN.x1) / 2, (SCREEN.y0 + SCREEN.y1) / 2, SCREEN.z + 0.04);
  const len = lens.distanceTo(to), hw = (SCREEN.x1 - SCREEN.x0) / 2, hh = (SCREEN.y1 - SCREEN.y0) / 2;
  for (const [k, kk] of [[1.0, 0.045], [0.78, 0.05], [0.52, 0.06]] as [number, number][]) {
    const geo = new THREE.CylinderGeometry(0.04, Math.SQRT2, len, 4, 1, true);
    geo.rotateY(Math.PI / 4); geo.translate(0, -len / 2, 0); geo.scale(hw * k, 1, hh * k);
    const m = new THREE.Mesh(geo, new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      uniforms: { col: { value: new THREE.Color('#fff0d4') }, k: { value: kk } },
      vertexShader: 'varying float vY; void main(){ vY = uv.y; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
      fragmentShader: 'uniform vec3 col; uniform float k; varying float vY; void main(){ float y = clamp(vY, 0.0, 1.0); gl_FragColor = vec4(col * k * (0.35 + 0.65 * y * y), 1.0); }',
    }));
    m.position.copy(lens);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, -1, 0), to.clone().sub(lens).normalize());
    m.userData.keep = true; m.userData.noShadow = true; m.renderOrder = 3;
    g.add(m);
  }
  g.userData.keep = true;
  return g;
}

// a framed poster for a course, on the left wall
function poster(course: string, colour: string) {
  return canvasTex(200, 280, (g) => {
    g.fillStyle = colour; g.fillRect(0, 0, 200, 280);
    g.strokeStyle = 'rgba(255,248,230,.8)'; g.lineWidth = 4; g.strokeRect(12, 12, 176, 256);
    courseIcon(g, course, 100, 112, 74, '#fff3d6');
    g.fillStyle = '#fff3d6'; g.textAlign = 'center'; g.font = '400 26px Georgia, serif'; g.fillText(course, 100, 206);
    g.font = '700 12px ui-rounded, system-ui, sans-serif'; g.fillText('P L A Z M I   U N I V E R S I T Y', 100, 240);
  });
}

function screeningRoom(g: THREE.Group): Built {
  const { W, D, wall: WALL } = PU;
  const marble = mat('#efe8dc', { rough: 0.35 }), marbleV = mat('#e2d8c8', { rough: 0.4 }), gold = mat('#d6b05a', { rough: 0.3, metal: 0.7 });
  const walnut = mat('#5a3a26', { rough: 0.5 }), velvet = mat('#9c2f45', { rough: 0.95 }), plum = mat('#7a2238', { rough: 1 });
  // the stage before the screen: warm boards on a marble front
  g.add(wbox(1.2, 0, 0.05, W - 1.2, 0.34, 1.35, surf({ kind: 'planks', color: '#8a5a3a', board: 0.14 })));
  g.add(wbox(1.15, 0, 1.3, W - 1.15, 0.36, 1.4, marble), wbox(1.1, 0.34, 1.32, W - 1.1, 0.4, 1.46, gold));
  // the screen, its picture a material of its own the page redraws
  const screenM = new THREE.MeshBasicMaterial({ color: '#efeae0' });
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(SCREEN.x1 - SCREEN.x0, SCREEN.y1 - SCREEN.y0), screenM);
  screen.position.set((SCREEN.x0 + SCREEN.x1) / 2, (SCREEN.y0 + SCREEN.y1) / 2, SCREEN.z); screen.userData.keep = true; screen.userData.noShadow = true; g.add(screen);
  g.add(wbox(SCREEN.x0 - 0.1, SCREEN.y0 - 0.1, 0.05, SCREEN.x1 + 0.1, SCREEN.y1 + 0.1, SCREEN.z - 0.005, mat('#1c1a20', { rough: 0.8 })));
  // the proscenium: marble pilasters, a frieze with the name over, velvet curtains drawn back to either side
  for (const x of [1.5, W - 1.5]) {
    g.add(wbox(x - 0.36, 0, 0, x + 0.36, 4.5, 0.36, marble));
    g.add(wbox(x - 0.44, 0, 0, x + 0.44, 0.42, 0.44, marbleV), wbox(x - 0.46, 4.3, 0, x + 0.46, 4.5, 0.46, marbleV));
    for (const dx of [-0.18, 0, 0.18]) g.add(wbox(x + dx - 0.035, 0.5, 0.36, x + dx + 0.035, 4.2, 0.38, marbleV));
  }
  g.add(wbox(1.0, 4.5, 0, W - 1.0, 4.98, 0.5, marble), wbox(1.0, 4.5, 0.48, W - 1.0, 4.56, 0.54, gold), wbox(1.0, 4.92, 0.48, W - 1.0, 4.98, 0.54, gold));
  const name = new THREE.Mesh(new THREE.PlaneGeometry(6.4, 0.34), mat('#ffffff', { map: letters([{ text: 'PLAZMI UNIVERSITY', size: 64, color: '#a8842f', font: 'Georgia, "Times New Roman", serif', weight: 700 }], 1400, 74, '#efe8dc'), rough: 0.5 }));
  name.position.set(W / 2, 4.74, 0.505); name.userData.noShadow = true; g.add(name);
  // the curtains: soft folds, tied back with gold rope, and a scalloped valance across the top
  for (const s of [-1, 1]) {
    const x0 = s < 0 ? 1.9 : SCREEN.x1 + 0.15;
    for (let i = 0; i < 6; i++) {
      const f = cyl(0.11, 0.15 + (i % 2) * 0.03, 3.95, i % 2 ? velvet : plum, 8);
      f.position.set(x0 + 0.13 + i * 0.13, 0.36 + 3.95 / 2, 0.3 + (i % 2) * 0.05); g.add(f);
    }
    const rope = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.035, 6, 16), gold); rope.position.set(x0 + 0.45, 1.6, 0.36); rope.rotation.x = Math.PI / 2; rope.scale.set(1, 0.5, 1); g.add(rope);
    g.add(at(sphere(0.07, gold, 8, 6), x0 + 0.45, 1.45, 0.62));
  }
  g.add(wbox(1.9, 4.18, 0.22, W - 1.9, 4.5, 0.46, velvet));
  for (let i = 0; i < 16; i++) { const sc = new THREE.Mesh(new THREE.CircleGeometry(0.29, 10, Math.PI, Math.PI), velvet); sc.position.set(1.9 + 0.29 + i * ((W - 3.8 - 0.58) / 15), 4.18, 0.465); g.add(sc); }
  g.add(wbox(1.9, 4.16, 0.44, W - 1.9, 4.2, 0.48, gold));

  // the rows: red velvet seats on walnut, an aisle down either side
  const shellM = mat('#6e4430', { rough: 0.5 }), iron = mat('#2b2622', { rough: 0.5, metal: 0.3 });
  const theatre: Seat[] = [];
  ROWS.forEach((zc, k) => {
    for (const x of SEAT_X) {
      const s = theatreSeat(velvet, shellM); s.position.set(x, 0, zc); g.add(s);
      // best first: the middle rows, the middle seats
      // (the second and third rows fill together, so a few watching sit in two rows, not one long one)
      const rank = [2, 0, 0.5, 2.5][k] + Math.abs(x - 6.5);
      theatre.push({ id: `r${k}s${x}`, at: [x, zc - 0.04], face: [x, -6], top: 0.48, from: [x, zc - 0.62], rank });
    }
    // the cast-iron ends between the seats, with walnut arms on top
    for (let i = 0; i <= SEAT_X.length; i++) {
      const x = SEAT_X[0] - 0.5 + i;
      g.add(wbox(x - 0.035, 0, zc - 0.28, x + 0.035, 0.62, zc + 0.3, iron), wbox(x - 0.055, 0.62, zc - 0.32, x + 0.055, 0.68, zc + 0.22, walnut));
    }
  });
  theatre.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));

  // the walls: marble pilasters down the left, sconces, and the courses' posters between
  const glowS = mat('#ffe7b8', { emissive: '#ffc27a', emissiveIntensity: 2.2 });
  const courses: [string, string][] = [['Architecture', '#3f6b7a'], ['Lisp', '#6a4a8a'], ['Dance', '#a8485e']];
  [3.0, 6.0, 9.0].forEach((z, i) => {
    const p = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 1.26), mat('#ffffff', { map: poster(courses[i][0], courses[i][1]), rough: 0.8 }));
    p.rotation.y = Math.PI / 2; p.position.set(0.07, 2.2, z); g.add(p);
    g.add(wbox(0.02, 1.52, z - 0.52, 0.06, 2.88, z + 0.52, gold));
    const sc = new THREE.Group(); sc.add(wbox(0, -0.1, -0.06, 0.08, 0.1, 0.06, gold));
    const sh = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.15, 0.24, 10, 1, true), glowS); sh.position.set(0.2, 0.08, 0); sh.userData.noShadow = true; sc.add(sh);
    sc.position.set(0.05, 3.55, z); g.add(sc);
  });
  for (const z of [1.5, 4.5, 7.5, 10.5]) g.add(wbox(0, 0, z - 0.25, 0.22, WALL - 0.14, z + 0.25, marble), wbox(0, 0, z - 0.3, 0.28, 0.3, z + 0.3, marbleV));
  for (const x of [0.55, W - 0.55]) { const sc = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.15, 0.24, 10, 1, true), glowS); sc.position.set(x, 3.4, 0.2); sc.userData.noShadow = true; g.add(sc); g.add(wbox(x - 0.06, 3.2, 0, x + 0.06, 3.32, 0.12, gold)); }
  // runners down the aisles
  g.add(at(F.rug({ w: 1.2, d: 8.4, field: '#8c2a3c', border: '#d6b05a', accent: '#f2c14e' } as any), 1.45, 0.02, 6.3));
  g.add(at(F.rug({ w: 1.3, d: 9.2, field: '#8c2a3c', border: '#d6b05a', accent: '#f2c14e' } as any), 11.5, 0.02, 6.0));

  // the projector at the back, its beam soft over everyone's heads
  const reels: THREE.Object3D[] = [];
  // (turned to look at the screen's middle, its lens where the beam starts)
  const pj = projector(gold, reels), yaw = Math.atan2(LENS[0] - (SCREEN.x0 + SCREEN.x1) / 2, LENS[2] - SCREEN.z), lo = 0.575 * PJ_S;
  pj.scale.setScalar(PJ_S); pj.rotation.y = yaw; pj.position.set(LENS[0] + lo * Math.sin(yaw), 0, LENS[2] + lo * Math.cos(yaw)); g.add(pj);
  const bm = beam(); g.add(bm);
  // a popcorn cart by the back wall, and a palm either side of the stage
  const cart = new THREE.Group();
  cart.add(at(rbox(0.8, 0.7, 0.55, 0.04, mat('#c8323a', { rough: 0.5 }), 2), 0, 0.55, 0));
  cart.add(at(rbox(0.82, 0.1, 0.57, 0.03, mat('#fbf5e8'), 1), 0, 0.92, 0));
  const glassBox = wbox(-0.36, 0.97, -0.24, 0.36, 1.5, 0.24, mat('#d9eef2', { opacity: 0.25, rough: 0.05 })); glassBox.userData.noShadow = true; cart.add(glassBox);
  const pop = mat('#f6e3a0', { rough: 0.9 });
  for (let i = 0; i < 22; i++) cart.add(at(sphere(0.07, pop, 6, 5), -0.28 + (i % 6) * 0.11, 1.02 + Math.floor(i / 6) * 0.07, -0.16 + ((i * 7) % 4) * 0.1));
  const roof = new THREE.Mesh(new THREE.ConeGeometry(0.5, 0.26, 4), mat('#c8323a')); roof.position.y = 1.63; roof.rotation.y = Math.PI / 4; cart.add(roof);
  for (const s of [-1, 1]) cart.add(at(cyl(0.12, 0.12, 0.05, iron, 12).rotateZ(Math.PI / 2), s * 0.42, 0.14, 0.12));
  cart.position.set(1.2, 0, 9.9); cart.rotation.y = 0.4; g.add(cart);
  for (const x of [0.75, W - 0.75]) g.add(at(F.plant({ kind: 'tree', pot: '#d9cfbd', scale: 1.4, seed: Math.round(x) } as any), x, 0, 1.0));
  // a stand by the door: what's on, in chalk
  const stand = new Board(0.8, 1.0, [240, 300], '#2c3a33');
  const st = new THREE.Group(), wood = mat('#6e4430', { rough: 0.6 });
  for (const s of [-1, 1]) st.add(at(cyl(0.025, 0.03, 1.5, gold, 6), s * 0.38, 0.75, 0));
  st.add(wbox(-0.45, 0.88, -0.03, 0.45, 1.98, 0.02, wood));
  stand.mesh.position.set(0, 1.43, 0.03); st.add(stand.mesh);
  st.position.set(9.4, 0, 9.8); st.rotation.y = 0.4; g.add(st);

  const screenL = warmLight('#cfdcff', 4, W / 2, 2.4, 2.4, 9);
  const lamp = warmLight('#ffe2b8', 5, W / 2, 4.2, 7.5, 12);
  const lampL = warmLight('#ffd8a0', 2.5, 0.8, 3.4, 6.0, 7);

  // what's on the screen: the course most in the rows are watching, or the university's title card
  let showing = false, live: { slug: string; lesson?: Lesson | null }[] = [], liveNow = 0, town: any = null, key = '';
  const redraw = () => {
    const now = liveNow || Date.now();
    const ls = live.map((p) => p.lesson).filter((l): l is Lesson => !!l && (!l.until || l.until > now));
    let show: Show | null = null;
    if (ls.length) {
      const by = new Map<string, Lesson[]>();
      for (const l of ls) by.set(l.course, [...(by.get(l.course) || []), l]);
      const order = [...by.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
      const [course, xs] = order[0];
      const n = [...xs].sort((a, b) => xs.filter((x) => x.n === b.n).length - xs.filter((x) => x.n === a.n).length || a.n - b.n)[0];
      show = { course, n: n.n, title: n.title, others: order.slice(1).map(([c]) => c) };
    } else if (town?.showing?.length) {
      // (between polls of who's here: the panel's list of who's watching what)
      const s0 = town.showing[0], words = String(s0.lesson ?? '');
      show = { course: s0.course, n: +(words.match(/\d+/)?.[0] || 1), title: /^\d+$/.test(words) ? '' : words, others: [...new Set<string>(town.showing.map((x: any) => x.course))].filter((c) => c !== s0.course) };
    }
    showing = !!show;
    const k = JSON.stringify(show);
    if (k === key) return; key = k;
    screenM.map?.dispose(); screenM.map = screenCard(show); screenM.needsUpdate = true;
    stand.set([{ text: 'NOW SHOWING', size: 34, color: '#f2c14e' }, ...(show ? [{ text: show.course, size: 44, color: '#f6f2e8' }, { text: `lesson ${show.n} of 3`, size: 32, color: '#cfd8cf' }]
      : [{ text: 'the title card', size: 36, color: '#f6f2e8' }, { text: 'take a seat', size: 30, color: '#cfd8cf' }])]);
  };
  redraw();

  return {
    // the stage, the pilasters, the rows of seats, the projector, the cart, the palms, the stand
    avoid: [{ x: 1.1, y: 0, w: W - 2.2, d: 1.5 }, { x: 0, y: 0, w: 0.4, d: D }, ...ROWS.map((z) => ({ x: SEAT_X[0] - 0.55, y: z - 0.32, w: SEAT_X.length + 0.1, d: 0.66 })),
      { x: LENS[0] - 0.75, y: LENS[2] - 0.1, w: 1.3, d: 1.3 }, { x: 0.7, y: 9.4, w: 1.0, d: 1.0 }, { x: W - 1.2, y: 0.5, w: 0.9, d: 0.9 }, { x: 9.2, y: 9.4, w: 0.8, d: 0.6 }],
    starts: [[6.0, 9.4], [8.6, 9.0], [11.6, 7.2], [1.6, 5.2], [7.0, 10.2], [11.4, 3.2], [2.0, 8.2]],
    theatre,
    idle: {
      place: 'pu',
      // standing about at the back, in the aisles and before the stage
      sites: [{ at: [3.2, 9.0], view: [6.5, 2.5] }, { at: [8.8, 9.6], view: [6.5, 9.0] }, { at: [4.6, 10.2], view: [6.5, 9.0] }, { at: [11.6, 8.0], view: [6.5, 5.0] },
        { at: [1.5, 6.6], view: [6.5, 5.0] }, { at: [11.7, 4.4], view: [6.5, 3.0] }, { at: [6.5, 2.2], view: [6.5, 8.0] }, { at: [6.8, 9.6], view: [3.9, 9.6] }],
      seats: [],
      // the doorway, and the way in down the right aisle
      lanes: [{ x0: PU.door[0], z0: D - 1.2, x1: PU.door[1], z1: D }, { x0: 10.9, z0: 1.6, x1: 12.1, z1: D }],
    },
    lights: [screenL, lamp, lampL],
    update: (d) => { town = d; redraw(); },
    hear: (present, now) => { live = present; liveNow = now; redraw(); },
    tick: (dt, t) => {
      if (showing) for (const r of reels) r.rotation.x -= dt * 1.6;
      // the beam breathes a little, as a lamp through a gate does
      const f = 1 + Math.sin(t * 7.3) * 0.04 + Math.sin(t * 2.1) * 0.03;
      for (const m of bm.children) (((m as THREE.Mesh).material as THREE.ShaderMaterial).uniforms.col.value as THREE.Color).setRGB(f, 0.94 * f, 0.83 * f);
    },
  };
}

// ---------------------------------------------------------------------------

const base = { W: W0, D: D0, wall: WALL0, door: [5.2, 7.8] as [number, number] };
export const SETS: Record<RoomId, RoomSet> = {
  library: { ...base, plaster: '#efe4cf', paper: { bg: '#2f5a4a', ink: '#3b6a57', ink2: '#e9c46a', kind: 'sprig' }, floor: { kind: 'planks', color: '#9a6a44', board: 0.16 }, wainscot: '#6e4430', trim: '#c7a24a', island: '#cdbfa8', windows: [2.0], build: readingRoom },
  post: { ...base, plaster: '#f3e6cf', paper: { bg: '#f6e7d2', ink: '#e9c8b0', ink2: '#c8553d', kind: 'stripe' }, floor: { kind: 'planks', color: '#c99862', board: 0.2 }, wainscot: '#7a1a18', trim: '#d6b05a', island: '#cdbfa8', windows: [2.6, 7.4], build: postOffice },
  gazette: { ...base, plaster: '#ece6da', paper: { bg: '#e7e2d6', ink: '#d2cbbb', ink2: '#1d4f91', kind: 'diamond' }, floor: { kind: 'planks', color: '#8a6a50', board: 0.22 }, wainscot: '#3a3a40', trim: '#9b2b2b', island: '#cdbfa8', windows: [], build: printShop },
  market: { ...base, plaster: '#f3e6cf', paper: { bg: '#f1dfc0', ink: '#e6cfa6', ink2: '#c8553d', kind: 'dots' }, floor: { kind: 'flagstone', color: '#cdbfa8' }, wainscot: '#7a4a2e', trim: '#c7a24a', island: '#cdbfa8', windows: [2.6, 7.4], build: marketHall },
  workshop: { ...base, plaster: '#efe4cf', paper: { bg: '#d9e2e6', ink: '#c3d0d6', ink2: '#5f8b9a', kind: 'stripe' }, floor: { kind: 'planks', color: '#b88a5a', board: 0.26 }, wainscot: '#5f7d6e', trim: '#8a5a3a', island: '#cdbfa8', windows: [8.4], build: workshop },
  bakery: { ...base, plaster: '#fbf1e4', paper: { bg: '#fde8ee', ink: '#f6c9d6', ink2: '#9fe3d0', kind: 'dots' }, floor: { kind: 'planks', color: '#c99862', board: 0.18 }, wainscot: '#9fd3c3', trim: '#fffaf0', island: '#cdbfa8', windows: [2.6, 7.4], build: cafe },
  townhall: { ...base, plaster: '#f3eee4', paper: { bg: '#e6e9f2', ink: '#d6dbe8', ink2: '#c7a24a', kind: 'stars' }, floor: { kind: 'planks', color: '#8a5a3a', board: 0.14 }, wainscot: '#2b3d5a', trim: '#d6b05a', island: '#cdbfa8', windows: [9.0], build: townHall },
  pu: { ...PU, frame: { dist: 45, y: 1.9 }, plaster: '#efe7da', paper: { bg: '#2f3f63', ink: '#3a4c74', ink2: '#c7a24a', kind: 'diamond' }, floor: { kind: 'planks', color: '#7a4e32', board: 0.14 }, wainscot: '#5a3a26', trim: '#efe8dc', island: '#cdbfa8', windows: [], build: screeningRoom },
};
